import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MousePointer2,
  ArrowUpRight,
  Spline,
  Type as TypeIcon,
  Lasso,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Sparkles,
  Link2,
  CheckCircle2,
  Lock,
  Loader2,
  AlertTriangle,
  Image as ImageIcon,
  Wand2,
  RefreshCw,
  X,
} from 'lucide-react';
import type {
  CanvasPrimitive,
  DrawingFigureSummary,
  DrawingNumeralRow,
} from '@/api/client';
import {
  verifyFigure as apiVerifyFigure,
  autoMapMatter as apiAutoMapMatter,
  freezeFigure as apiFreezeFigure,
  unfreezeFigure as apiUnfreezeFigure,
  vectorizeDrawingFigure,
  getDrawingFigureSvg,
  type VerifyIssue,
  type VerifyFigureResponse,
} from '@/api/client';
import DOMPurify from 'dompurify';
import {
  checkForCrossTabWrite,
  hydrateUndoStacksFromStorage,
  useDrawingStore,
  type DrawingTool,
} from '@/store/drawingStore';
// Phase 3 — NumeralPlaceModal + NumeralPlaceResult retired with
// the # tool. The component file is deleted in this commit.
import VectorEditSurface from './VectorEditSurface';
import { isRasterAnchor } from './_canvas_geom';
import { RegenerateFigurePanel } from './RegenerateFigurePanel';
import {
  DRAWING_TERM_DRAG_TYPE,
  DRAWING_NUMERAL_DRAG_TYPE,
  type DraggedTermPayload,
  type DraggedNumeralPayload,
} from './ReferencePanel';

interface Props {
  matterId: string;
  figure: DrawingFigureSummary | null;
}

/**
 * Canvas surface (Phase 6D).
 *
 * Three layered regions stacked top-to-bottom:
 *   1. Toolbar — six tools (cursor / numeral / arrow / text / lasso
 *      / zoom). Click switches `activeTool` in the store.
 *   2. Surface — figure raster + SVG overlay. The overlay carries
 *      every canvas primitive (numeral chips, arrows, text, lasso
 *      polygons). Drag handlers run via pointermove/pointerup.
 *   3. Action bench — Analyze / Auto-map / Verify / Freeze. Phase 8
 *      wires those; they stay disabled in this slice.
 *   4. Status strip — origin · analyze status · numerals · etc.
 *
 * Numeral-place flow is half-wired here: clicking the canvas with the
 * `numeral` tool active emits `pendingPlacementCoords` upward via the
 * `onRequestNumeralPlace` callback, which slice 6E uses to open the
 * numeral-place modal. For Phase 6D specifically that callback is
 * undefined (modal arrives next slice), and the pending coords are
 * stored locally so a 6D-only smoke run can verify the click handler.
 */
export default function Canvas({ matterId, figure }: Props) {
  const activeTool = useDrawingStore((s) => s.activeTool);
  const setActiveTool = useDrawingStore((s) => s.setActiveTool);
  const zoomLevel = useDrawingStore((s) => s.zoomLevel);
  const setZoom = useDrawingStore((s) => s.setZoom);
  const canvasPrimitives = useDrawingStore((s) => s.canvasPrimitives);
  const numerals = useDrawingStore((s) => s.numerals);
  const figures = useDrawingStore((s) => s.figures);
  const termMappings = useDrawingStore((s) => s.termMappings);
  const upsertCanvasPrimitive = useDrawingStore((s) => s.upsertCanvasPrimitive);
  // Phase 3 — createNumeralRegistryRow / patchNumeralRegistryRow
  // store reads dropped; the only Canvas consumer (onPlaceSubmit)
  // is gone. The actions themselves stay on the store for the
  // Numerals-tab edit flow (ReferencePanel uses the API client
  // directly today; the store actions are kept as future helpers).
  const undoCanvas = useDrawingStore((s) => s.undoCanvas);
  const redoCanvas = useDrawingStore((s) => s.redoCanvas);
  const flushCanvasNow = useDrawingStore((s) => s.flushCanvasNow);
  const canUndo = useDrawingStore((s) => s.canUndo);
  const canRedo = useDrawingStore((s) => s.canRedo);
  const pendingPlaceRequest = useDrawingStore((s) => s.pendingPlaceRequest);
  const clearNumeralPlaceRequest = useDrawingStore((s) => s.clearNumeralPlaceRequest);
  const numeralFocusRequest = useDrawingStore((s) => s.numeralFocusRequest);
  const clearNumeralFocusRequest = useDrawingStore((s) => s.clearNumeralFocusRequest);
  // Phase 3 — placementMode / pendingNumeralLabel /
  // cancelNumeralPlacement store reads retired with the # tool.
  // Other deletions: pendingPlace state + NumeralPlaceModal +
  // window Escape handler. Existing chips on legacy data still
  // render via NumeralChip; the # interaction surface is gone.
  const selectedPrimitiveIds = useDrawingStore((s) => s.selectedPrimitiveIds);
  const clearSelection = useDrawingStore((s) => s.clearSelection);
  const deletePrimitives = useDrawingStore((s) => s.deletePrimitives);

  const primitives = figure ? canvasPrimitives[figure.id] || [] : [];

  /** Slice 6G — chip id transiently highlighted via the
   * "scroll-to-chip" request from the Numerals tab. The Surface
   * reads this and adds a pulse ring for ~1500ms before clearing. */
  const [focusedChipId, setFocusedChipId] = useState<string | null>(null);

  // ── Hydrate undo stacks once on first canvas mount. HR-P1.3:
  //    now async (IDB-backed) but fire-and-forget — the store's
  //    setState is internally synchronous, so re-renders happen
  //    after the IDB read completes without us awaiting.
  useEffect(() => { void hydrateUndoStacksFromStorage(); }, []);

  // ── HR-P2.2 — two-tab last-write-wins detection. On window
  //    focus, refetch the active figure's canvas state and
  //    compare ``figure_updated_at`` against the local cache.
  //    When the server is ahead (another tab wrote while we
  //    were blurred), `checkForCrossTabWrite` hydrates the new
  //    state + fires the "Canvas updated in another tab" toast.
  //    Listener stays registered for the lifetime of the
  //    Canvas mount; clean-up on unmount.
  useEffect(() => {
    if (!figure) return;
    const onFocus = () => {
      void checkForCrossTabWrite(matterId, figure.id);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [matterId, figure]);

  // Phase 3 — the old "+ New" panel opener fired
  // setPendingPlace via pendingPlaceRequest; the place modal flow
  // is gone. Keep the request consumer for now to drain stale
  // signals, but don't open anything.
  useEffect(() => {
    if (!pendingPlaceRequest || !figure) return;
    clearNumeralPlaceRequest();
  }, [pendingPlaceRequest, figure, clearNumeralPlaceRequest]);

  // ── Slice 6G: react to the panel's "scroll to chip" request.
  // We only act when the request targets the active figure;
  // otherwise we no-op (the drafter still needs to navigate to the
  // figure that holds the chip). The pulse clears after 1500ms.
  useEffect(() => {
    if (!numeralFocusRequest || !figure) return;
    if (numeralFocusRequest.figureId !== figure.id) {
      clearNumeralFocusRequest();
      return;
    }
    setFocusedChipId(numeralFocusRequest.primitiveId);
    clearNumeralFocusRequest();
    const t = setTimeout(() => setFocusedChipId(null), 1500);
    return () => clearTimeout(t);
  }, [numeralFocusRequest, figure, clearNumeralFocusRequest]);

  // Phase 3 — pendingPlace coordinate state retired with the
  // NumeralPlaceModal. Numerals enter the registry via Analyze
  // and (Phase 4) auto-map's text-annotation pass; they don't
  // need a click-driven coordinate at the canvas any more.
  /** Arrow tool state machine: first click sets `arrowStart`,
   * second click commits the arrow primitive. */
  const [arrowStart, setArrowStart] = useState<{ x: number; y: number } | null>(null);
  /** BUG-019d — same two-click state machine for the curved
   * leader-line tool. Stored separately from `arrowStart` so the
   * tool toggle resets cleanly. */
  const [curvedArrowStart, setCurvedArrowStart] = useState<{ x: number; y: number } | null>(null);
  /** Text tool: click sets coords; an inline input renders at the
   * point and commits on Enter / blur. */
  const [pendingText, setPendingText] = useState<{ x: number; y: number } | null>(null);
  const [pendingTextValue, setPendingTextValue] = useState('');
  /** BUG-019b / BUG-029 — drafter-selectable font size for new text
   * primitives. The S/M/L/XL segmented control in the toolbar
   * (only visible while the Text tool is active) writes to this
   * state; ``commitPendingText`` stores it on the primitive's
   * ``primitive_data_json.fontSize``. Default is 16 (the cue's
   * "M" preset) — bigger than the previous implicit 14 so plain-
   * vanilla text drops are legible at 100% zoom. */
  const [textFontSize, setTextFontSize] = useState<number>(16);

  // BUG-032 — vector view toggle. Reset to false on figure change so
  // the new figure starts in raster view; the toolbar toggle is
  // visible only when ``figure.is_vectorized``. After a successful
  // vectorize action (in ActionBench) the upserted figure flips
  // is_vectorized=true, the toggle button appears, and a one-shot
  // effect (below) auto-enables the view so the drafter sees their
  // SVG immediately.
  const [vectorView, setVectorView] = useState<boolean>(false);
  // Mode-toggle busy flag — held while we flush pending canvas
  // mutations to the backend before swapping Surface components.
  // Prevents a rapid double-toggle from racing the flush.
  const [modeToggling, setModeToggling] = useState<boolean>(false);
  const prevVectorizedRef = useRef<boolean>(false);
  useEffect(() => {
    setVectorView(false);
    prevVectorizedRef.current = !!figure?.is_vectorized;
  }, [figure?.id]);
  useEffect(() => {
    const isNow = !!figure?.is_vectorized;
    if (isNow && !prevVectorizedRef.current) {
      setVectorView(true);
    }
    prevVectorizedRef.current = isNow;
  }, [figure?.is_vectorized]);


  const onCanvasClick = useCallback((coords: { x: number; y: number }) => {
    if (!figure) return;
    // Frozen figures are read-only for any mutation tool. Surface a
    // toast so the drafter sees why the click did nothing.
    if (figure.is_frozen
        && (activeTool === 'arrow'
            || activeTool === 'curved_arrow'
            || activeTool === 'text')) {
      useDrawingStore.getState().showToast(
        'This figure is frozen — click Unfreeze in the action bar before adding annotations.',
      );
      return;
    }
    if (activeTool === 'arrow') {
      if (arrowStart === null) {
        setArrowStart(coords);
      } else {
        upsertCanvasPrimitive(matterId, figure.id, {
          primitive_type: 'arrow',
          primitive_data_json: {
            x1: arrowStart.x, y1: arrowStart.y,
            x2: coords.x,     y2: coords.y,
          },
          z_index: primitives.length,
        });
        setArrowStart(null);
      }
      return;
    }
    if (activeTool === 'curved_arrow') {
      // BUG-019d — two-click bezier. First click sets the numeral
      // end (start), second click sets the element end and commits
      // with an auto-computed perpendicular control point.
      if (curvedArrowStart === null) {
        setCurvedArrowStart(coords);
      } else {
        const { cx, cy } = defaultBezierControl(
          curvedArrowStart.x, curvedArrowStart.y, coords.x, coords.y,
        );
        upsertCanvasPrimitive(matterId, figure.id, {
          primitive_type: 'curved_arrow',
          primitive_data_json: {
            x1: curvedArrowStart.x, y1: curvedArrowStart.y,
            x2: coords.x,           y2: coords.y,
            cx, cy,
          },
          z_index: primitives.length,
        });
        setCurvedArrowStart(null);
      }
      return;
    }
    if (activeTool === 'text') {
      setPendingText(coords);
      setPendingTextValue('');
      return;
    }
    // Lasso uses the drag rectangle (mousedown→mouseup) handled in
    // Surface; bare clicks with the lasso tool are no-ops.
  }, [activeTool, arrowStart, curvedArrowStart, figure, matterId,
      primitives.length, upsertCanvasPrimitive]);

  const commitPendingText = useCallback(() => {
    if (!figure || !pendingText) return;
    const text = pendingTextValue.trim();
    if (text) {
      upsertCanvasPrimitive(matterId, figure.id, {
        primitive_type: 'text',
        primitive_data_json: {
          x: pendingText.x, y: pendingText.y, text,
          fontSize: textFontSize,
        },
        z_index: primitives.length,
      });
    }
    setPendingText(null);
    setPendingTextValue('');
  }, [figure, matterId, pendingText, pendingTextValue, primitives.length,
      textFontSize, upsertCanvasPrimitive]);

  // ── Keyboard shortcuts (focus-scoped, brief §7) ─────────────────
  const onCanvasKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!figure) return;
    const isMod = e.ctrlKey || e.metaKey;
    if (!isMod) {
      if (e.key === 'Escape') {
        setArrowStart(null);
        setCurvedArrowStart(null);
        setPendingText(null);
        clearSelection();
      }
      return;
    }
    const key = e.key.toLowerCase();
    if (key === 'z' && !e.shiftKey) {
      e.preventDefault();
      if (canUndo(figure.id)) undoCanvas(matterId, figure.id);
    } else if ((key === 'z' && e.shiftKey) || key === 'y') {
      e.preventDefault();
      if (canRedo(figure.id)) redoCanvas(matterId, figure.id);
    }
  }, [figure, canUndo, canRedo, undoCanvas, redoCanvas, matterId,
      clearSelection]);
  // Phase 3 — onPlaceSubmit retired with NumeralPlaceModal. The
  // three-flow chain (reuse_existing_id / patch_existing_id /
  // fresh) is gone; numerals enter the registry via Analyze
  // (vision) or auto-map (Phase 4 text-annotation pass) rather
  // than via a click + modal.

  const onTermDrop = useCallback(
    (coords: { x: number; y: number }, term: DraggedTermPayload) => {
      if (!figure) return;
      upsertCanvasPrimitive(matterId, figure.id, {
        primitive_type: 'text',
        primitive_data_json: { x: coords.x, y: coords.y, text: term.name },
        z_index: primitives.length,
      });
    },
    [figure, matterId, primitives.length, upsertCanvasPrimitive],
  );

  // PD-005 — companion drop path for a registry numeral row dragged
  // out of the Numerals tab. Stores the registry id on
  // ``drawing_numeral_id`` so the rendered chip stays in sync with
  // the registry row's label / claim binding through the standard
  // ``numerals.find(n => n.id === ...)`` lookup. The numeral +
  // label fields on ``primitive_data_json`` are a fallback for
  // the case where the registry row gets deleted and the chip
  // becomes orphaned — the chip still renders its own token + a
  // best-effort label so the drafter can spot the dangling
  // reference.
  const onNumeralDrop = useCallback(
    (
      coords: { x: number; y: number },
      payload: DraggedNumeralPayload,
    ) => {
      if (!figure) return;
      upsertCanvasPrimitive(matterId, figure.id, {
        primitive_type: 'numeral',
        primitive_data_json: {
          x: coords.x,
          y: coords.y,
          numeral: payload.numeral,
          label: payload.label,
        },
        drawing_numeral_id: payload.numeralId,
        z_index: primitives.length,
      });
    },
    [figure, matterId, primitives.length, upsertCanvasPrimitive],
  );

  // BUG-027 + BUG-031 — viewport-locked canvas with transform-based
  // pan.
  //   * viewportRef points at the overflow-hidden wrapper that
  //     clips the figure to the centre column.
  //   * pan{X,Y} translate the figure inside the viewport — replaces
  //     the previous ``scrollLeft``/``scrollTop`` approach so the
  //     viewport itself never scrolls (BUG-031: page used to scroll
  //     when canvas content overflowed).
  //   * fitMode === 'fit' resets BOTH zoom and pan to fit the figure
  //     inside the viewport (initial load, Fit button click).
  //   * fitMode === 'centre' resets only pan (keeps the drafter's
  //     manual zoom intact when switching between figures).
  //   * hasFittedRef avoids double-fitting on the first figure load —
  //     React Strict Mode + the image onLoad both fire and we want
  //     a single net effect.
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const intrinsicSizeRef = useRef<{ w: number; h: number } | null>(null);
  const hasFittedRef = useRef(false);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);

  const applyFit = useCallback((mode: 'fit' | 'centre' = 'fit') => {
    const viewport = viewportRef.current;
    const intrinsic = intrinsicSizeRef.current;
    if (!viewport || !intrinsic) return;
    const { w: iw, h: ih } = intrinsic;
    if (iw <= 0 || ih <= 0) return;
    let z = zoomLevel;
    if (mode === 'fit') {
      // Subtract a small margin so the figure doesn't sit flush
      // against the viewport edges.
      const vw = Math.max(1, viewport.clientWidth - 32);
      const vh = Math.max(1, viewport.clientHeight - 32);
      z = Math.min(vw / iw, vh / ih, 1);
      // Clamp matches setZoom's bounds (0.25 .. 4) so we don't
      // dodge the store-side guard.
      z = Math.max(0.25, Math.min(4, z));
      setZoom(z);
    }
    // Schedule the centre-pan for the next frame so the new zoom
    // (which mutates renderW/renderH on the next render) has
    // landed in the DOM before we measure clientWidth.
    requestAnimationFrame(() => {
      const v = viewportRef.current;
      if (!v) return;
      const renderW = iw * z;
      const renderH = ih * z;
      // Pan can be negative when the figure is larger than the
      // viewport — that's correct, the figure overhangs and the
      // drafter pans by drag to scroll within it.
      setPanX(Math.round((v.clientWidth - renderW) / 2));
      setPanY(Math.round((v.clientHeight - renderH) / 2));
    });
  }, [zoomLevel, setZoom]);

  // Reset the fit-once latch every time the active figure changes
  // so the next figure's first load triggers a full fit.
  useEffect(() => {
    hasFittedRef.current = false;
  }, [figure?.id]);

  const onIntrinsicSize = useCallback((w: number, h: number) => {
    intrinsicSizeRef.current = { w, h };
    if (!hasFittedRef.current) {
      hasFittedRef.current = true;
      applyFit('fit');
    } else {
      applyFit('centre');
    }
  }, [applyFit]);

  // BUG-031 — wheel-zoom on the canvas viewport. React's wheel
  // events register passive=true by default, so e.preventDefault()
  // inside a React handler is silently ignored and the page scrolls.
  // Attach a native non-passive listener so the wheel zooms instead.
  useEffect(() => {
    const v = viewportRef.current;
    if (!v) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const cur = useDrawingStore.getState().zoomLevel;
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      const next = Math.max(0.25, Math.min(4, cur * factor));
      useDrawingStore.getState().setZoom(next);
    };
    v.addEventListener('wheel', handler, { passive: false });
    return () => v.removeEventListener('wheel', handler);
  }, []);

  // BUG-031 — drag-to-pan on the viewport. Active only under the
  // cursor tool; chip / handle / lasso pointerdowns either
  // stop-propagate or are gated on a different ``activeTool``, so
  // they don't accidentally start a pan.
  const panStateRef = useRef<{
    startX: number; startY: number; baseX: number; baseY: number;
  } | null>(null);
  const onViewportPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (activeTool !== 'cursor') return;
      // BUG-032 follow-up — don't start a pan when the click
      // originated on a vector-edit-selectable element. Capturing
      // the pointer on the viewport here would steal the click
      // from VectorEditSurface (the synthesised click target needs
      // pointerdown + pointerup to land on the same element; with
      // capture, pointerup gets retargeted to the viewport and the
      // click is suppressed). Drafters can still pan by clicking
      // the empty white viewport area outside the figure.
      const target = e.target as Element | null;
      if (target?.closest?.('[data-svg-edit-id]')) return;
      panStateRef.current = {
        startX: e.clientX, startY: e.clientY,
        baseX: panX, baseY: panY,
      };
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    }, [activeTool, panX, panY],
  );
  const onViewportPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!panStateRef.current) return;
      const { startX, startY, baseX, baseY } = panStateRef.current;
      setPanX(baseX + (e.clientX - startX));
      setPanY(baseY + (e.clientY - startY));
    }, [],
  );
  const onViewportPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!panStateRef.current) return;
      panStateRef.current = null;
      (e.currentTarget as Element).releasePointerCapture?.(e.pointerId);
    }, [],
  );

  // Window-scoped Escape — clears the armed numeral from anywhere
  // Phase 3 — window-scoped Esc handler for placementMode is gone
  // along with the # tool. The local onCanvasKeyDown handler still
  // covers Escape for arrow/curved-arrow/text starts.

  return (
    <div
      className="flex h-full min-h-0 flex-col focus:outline-none"
      data-testid="canvas"
      tabIndex={0}
      onKeyDown={onCanvasKeyDown}
    >
      <Toolbar
        activeTool={activeTool} onToolChange={setActiveTool}
        zoomLevel={zoomLevel} onZoomChange={setZoom}
        onFitToScreen={() => applyFit('fit')}
        canUndo={figure ? canUndo(figure.id) : false}
        canRedo={figure ? canRedo(figure.id) : false}
        onUndo={() => figure && undoCanvas(matterId, figure.id)}
        onRedo={() => figure && redoCanvas(matterId, figure.id)}
        textFontSize={textFontSize}
        onTextFontSizeChange={setTextFontSize}
        figureIsVectorized={!!figure?.is_vectorized}
        vectorView={vectorView}
        vectorToggleBusy={modeToggling}
        onToggleVectorView={async () => {
          // BUG-3 — flush pending canvas mutations before
          // swapping Surface components. Previously the handler
          // only flipped the boolean; the autosave queue could
          // still hold a freshly-drawn primitive, and the swap
          // unmounted the Surface that owned the pending writes
          // — if the drafter navigated away immediately after
          // the toggle, the arrow / chip / text node was lost.
          // Flush + gate against double-toggle while in-flight.
          if (modeToggling) return;
          setModeToggling(true);
          try {
            try {
              await flushCanvasNow();
            } catch (exc) {
              // Non-fatal: the toggle proceeds even if the
              // flush failed (drafters get a recovery path
              // via the existing autosave retry loop). Log so
              // the failure shows up in DevTools.
              // eslint-disable-next-line no-console
              console.warn(
                '[canvas] flushCanvasNow before vector toggle '
                + 'failed; toggling anyway:', exc,
              );
            }
            // FT-003 (A3) — raster staleness warning. When
            // toggling back to raster after vector edits, the
            // PNG bytes on disk are from the pre-edit
            // re-vectorize pass; the drafter would see an
            // out-of-date image with no signal. ``source_stale``
            // is the existing flag the backend bumps whenever
            // the source has drifted ahead of the raster. We
            // surface a one-shot toast on the vector→raster
            // transition rather than on every toggle.
            setVectorView((prev) => {
              const next = !prev;
              if (prev === true && next === false
                  && figure?.source_stale) {
                useDrawingStore.getState().showToast(
                  'Raster preview is from a previous version — '
                  + 'click Re-vectorize on the action bar to refresh.',
                );
              }
              return next;
            });
          } finally {
            setModeToggling(false);
          }
        }}
      />
      <div
        ref={viewportRef}
        className={[
          'relative flex-1 overflow-hidden',
          // BUG-031 grab/grabbing for cursor tool pan affordance.
          activeTool === 'cursor'
            ? 'cursor-grab active:cursor-grabbing'
            : '',
        ].join(' ')}
        data-testid="canvas-scroll"
        onPointerDown={onViewportPointerDown}
        onPointerMove={onViewportPointerMove}
        onPointerUp={onViewportPointerUp}
        onPointerCancel={onViewportPointerUp}
      >
        {/* BUG-031 — pan layer. Translates the figure inside the
            viewport via CSS transform so the viewport itself never
            scrolls. ``willChange: transform`` keeps the layer
            promoted on a separate compositor layer for smooth
            drag updates. */}
        <div
          className="absolute left-0 top-0"
          style={{
            transform: `translate3d(${panX}px, ${panY}px, 0)`,
            willChange: 'transform',
          }}
          data-testid="canvas-pan-layer"
        >
          <Surface
            matterId={matterId}
            figure={figure}
            primitives={primitives}
            numerals={numerals}
            activeTool={activeTool}
            zoomLevel={zoomLevel}
            focusedChipId={focusedChipId}
            onTermDrop={onTermDrop}
            onNumeralDrop={onNumeralDrop}
            arrowStart={arrowStart}
            curvedArrowStart={curvedArrowStart}
            pendingText={pendingText}
            pendingTextValue={pendingTextValue}
            pendingTextFontSize={textFontSize}
            onPendingTextChange={setPendingTextValue}
            onCommitPendingText={commitPendingText}
            onCancelPendingText={() => { setPendingText(null); setPendingTextValue(''); }}
            onCanvasClick={onCanvasClick}
            onIntrinsicSize={onIntrinsicSize}
            vectorView={vectorView}
            onMovePrimitive={(p, x, y) => {
              if (!figure) return;
              upsertCanvasPrimitive(matterId, figure.id, {
                id: p.id,
                primitive_type: p.primitive_type,
                primitive_data_json: { ...p.primitive_data_json, x, y },
                z_index: p.z_index,
                drawing_numeral_id: p.drawing_numeral_id,
              });
            }}
            onUpdatePrimitiveData={(p, partial) => {
              if (!figure) return;
              upsertCanvasPrimitive(matterId, figure.id, {
                id: p.id,
                primitive_type: p.primitive_type,
                primitive_data_json: { ...p.primitive_data_json, ...partial },
                z_index: p.z_index,
                drawing_numeral_id: p.drawing_numeral_id,
              });
            }}
          />
        </div>
      </div>
      <ActionBench matterId={matterId} figure={figure} />
      {/* Verify-findings auto-popup panel was retired after the
          BUG-DRAW-007 second-click regression. The status strip
          below now owns the verify-results surface: it shows
          "verify clean" / "N issues" as a count + reveals the
          per-issue list as a status-strip-anchored popover when
          the drafter clicks. No modal, no dismiss-sig logic. */}
      <StatusStrip
        figure={figure}
        primitives={primitives}
        numerals={numerals}
        figures={figures}
      />
    </div>
  );
}

// ─── Toolbar ─────────────────────────────────────────────────────

interface ToolDef {
  id: DrawingTool;
  label: string;
  shortcut: string;
  icon: React.ReactNode;
  /** Hover tooltip; falls back to "{label} · {shortcut}" when absent. */
  tooltip?: string;
}

// Toolbar carries the v1 essentials plus the annotation tools
// drafters asked for in user testing. Arrow + Text + curved-arrow
// are workbench-only: they persist in drawing_canvas_state but
// never reach an export. Confirmed at audit time:
//   * drawing_pdf.py reads only raster_path — never canvas_state.
//   * drawing_docx.py iterates canvas_state but filters on
//     drawing_numeral_id, which is null for arrow/text/curved/lasso
//     primitives, so non-numeral rows skip naturally.
// Lasso is the bulk-selection tool: drag a rect, hit Delete to
// remove the matched primitives in one undo-friendly step.
const TOOLS: ToolDef[] = [
  { id: 'cursor',  label: 'Select & pan',   shortcut: 'V', icon: <MousePointer2 className="h-4 w-4" />,
    tooltip: 'Click to select a primitive; drag a primitive to move; drag empty space to pan.' },
  // Phase 3 — # numeral tool retired. Numerals enter the registry
  // via Analyze (vision extraction) or auto-map (text-annotation
  // pass landing in Phase 4). Existing chips on legacy matters
  // continue to render via NumeralChip, unchanged.
  { id: 'arrow',   label: 'Draw arrow',     shortcut: 'A', icon: <ArrowUpRight className="h-4 w-4" />,
    tooltip: 'Click start, then click end to draw an arrow. Annotations are workbench-only — they won’t appear in exports.' },
  // BUG-019d (re-introduced) — curved leader line. Two-click bezier:
  // click the numeral end (tail), then the element end (head); the
  // control point is auto-seeded perpendicular to the midpoint and
  // can be dragged afterwards via the cursor-tool handles.
  { id: 'curved_arrow', label: 'Curved arrow', shortcut: 'C', icon: <Spline className="h-4 w-4" />,
    tooltip: 'Click numeral end, then element end — bezier curve seeds automatically. Drag the midpoint handle to reshape.' },
  { id: 'text',    label: 'Add text',       shortcut: 'T', icon: <TypeIcon className="h-4 w-4" />,
    tooltip: 'Click to drop a text annotation. Annotations are workbench-only — they won’t appear in exports.' },
  { id: 'lasso',   label: 'Multi-select',   shortcut: 'L', icon: <Lasso className="h-4 w-4" />,
    tooltip: 'Drag a rectangle to multi-select primitives (does NOT pan), then press Delete or Backspace to remove them.' },
  { id: 'zoom',    label: 'Zoom',           shortcut: 'Z', icon: <ZoomIn className="h-4 w-4" />,
    tooltip: 'Use the +/− buttons to zoom.' },
];

/** BUG-019b / BUG-029 — S/M/L/XL preset font sizes for new text
 * primitives. Surfaces only when the Text tool is active so the
 * toolbar stays compact for every other tool. The mapping mirrors
 * the cue (S=12, M=16, L=20, XL=28). */
const TEXT_SIZE_PRESETS: { label: string; px: number }[] = [
  { label: 'S',  px: 12 },
  { label: 'M',  px: 16 },
  { label: 'L',  px: 20 },
  { label: 'XL', px: 28 },
];

export function Toolbar({
  activeTool, onToolChange, zoomLevel, onZoomChange, onFitToScreen,
  canUndo, canRedo, onUndo, onRedo,
  textFontSize, onTextFontSizeChange,
  figureIsVectorized, vectorView, vectorToggleBusy = false,
  onToggleVectorView,
}: {
  activeTool: DrawingTool;
  onToolChange: (t: DrawingTool) => void;
  zoomLevel: number;
  onZoomChange: (z: number) => void;
  /** BUG-027 — reset zoom + scroll to fit the figure inside the
   * viewport. Sits between the zoom controls per the cue. */
  onFitToScreen: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  textFontSize: number;
  onTextFontSizeChange: (px: number) => void;
  /** BUG-032 — gate the Vector view toggle on whether the figure
   * has a traced SVG. The button stays mounted but disabled
   * (with explanatory tooltip) when the figure isn't vectorized,
   * so the affordance is discoverable. */
  figureIsVectorized: boolean;
  vectorView: boolean;
  /** Busy flag set while the parent is flushing pending canvas
   *  mutations before swapping Surface components. The button
   *  goes disabled to prevent a rapid double-toggle from racing
   *  the in-flight flush. Defaults to false. */
  vectorToggleBusy?: boolean;
  onToggleVectorView: () => void;
}) {
  return (
    <div
      className="flex items-center gap-1 border-b border-zinc-200 bg-white px-3 py-2"
      data-testid="canvas-toolbar"
    >
      {TOOLS.map((tool) => (
        <button
          key={tool.id}
          type="button"
          className={[
            'inline-flex h-8 w-8 items-center justify-center rounded-sm border text-zinc-600 transition-colors',
            activeTool === tool.id
              ? 'border-[#002FA7] bg-[#002FA7]/10 text-[#002FA7]'
              : 'border-transparent hover:bg-zinc-50',
          ].join(' ')}
          onClick={() => onToolChange(tool.id)}
          aria-pressed={activeTool === tool.id}
          title={tool.tooltip
            ? `${tool.label} · ${tool.shortcut}\n${tool.tooltip}`
            : `${tool.label} · ${tool.shortcut}`}
          data-testid={`tool-${tool.id}`}
        >
          {tool.icon}
        </button>
      ))}
      <div className="ml-3 flex items-center gap-1 border-l border-zinc-200 pl-3">
        <button
          type="button"
          className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-transparent text-zinc-500 hover:bg-zinc-50"
          onClick={() => onZoomChange(zoomLevel / 1.10)}
          title="Zoom out (10%)"
          data-testid="zoom-out"
        >
          <ZoomOut className="h-3.5 w-3.5" />
        </button>
        <span className="min-w-[3rem] text-center text-[11px] font-medium text-zinc-600 tabular-nums">
          {Math.round(zoomLevel * 100)}%
        </span>
        <button
          type="button"
          className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-transparent text-zinc-500 hover:bg-zinc-50"
          onClick={onFitToScreen}
          title="Fit figure to viewport"
          aria-label="Fit figure to viewport"
          data-testid="fit-to-screen"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-transparent text-zinc-500 hover:bg-zinc-50"
          onClick={() => onZoomChange(zoomLevel * 1.10)}
          title="Zoom in (10%)"
          data-testid="zoom-in"
        >
          <ZoomIn className="h-3.5 w-3.5" />
        </button>
      </div>
      {/* BUG-032 — Vector view toggle. Visible whenever a figure
          is selected; disabled (with tooltip) when the figure
          hasn't been vectorized yet so drafters discover the
          ActionBench Vectorize button rather than wondering why
          nothing happens. */}
      <div className="ml-3 flex items-center gap-1 border-l border-zinc-200 pl-3">
        <button
          type="button"
          className={[
            'inline-flex h-7 px-2 items-center justify-center gap-1 rounded-sm border text-[11px] font-medium',
            vectorView
              ? 'border-[#002FA7] bg-[#002FA7]/10 text-[#002FA7]'
              : 'border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50',
            'disabled:cursor-not-allowed disabled:opacity-40',
          ].join(' ')}
          disabled={!figureIsVectorized || vectorToggleBusy}
          onClick={onToggleVectorView}
          title={!figureIsVectorized
            ? 'Click "Vectorize" in the action bar below to enable.'
            : vectorToggleBusy
              ? 'Saving pending changes…'
              : vectorView
                ? 'Currently in Vector mode — click to switch to Preview (what export will produce).'
                : 'Currently in Preview mode — click to switch to Vector (click an SVG element + Delete to remove it).'}
          aria-pressed={vectorView}
          data-testid="vector-view-toggle"
        >
          {/* BUG-032 follow-up — label = CURRENT mode (not target),
              standard toggle convention. ``aria-pressed`` already
              communicates state to AT; the visible label now matches
              what the drafter sees on the canvas. "Preview" replaces
              "Raster" since the non-vector mode now renders the live
              SVG inline (when available) — it's no longer just a
              cached PNG. */}
          {vectorView ? 'Vector' : 'Preview'}
        </button>
      </div>
      {activeTool === 'text' && (
        <div
          className="ml-3 flex items-center gap-0.5 border-l border-zinc-200 pl-3"
          data-testid="text-size-presets"
          role="radiogroup"
          aria-label="Text size"
        >
          <span className="mr-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            Size
          </span>
          {TEXT_SIZE_PRESETS.map((preset) => {
            const active = textFontSize === preset.px;
            return (
              <button
                key={preset.label}
                type="button"
                role="radio"
                aria-checked={active}
                className={[
                  'inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-sm border px-1.5 text-[11px] font-medium transition-colors',
                  active
                    ? 'border-[#002FA7] bg-[#002FA7]/10 text-[#002FA7]'
                    : 'border-transparent text-zinc-600 hover:bg-zinc-50',
                ].join(' ')}
                onClick={() => onTextFontSizeChange(preset.px)}
                title={`${preset.label} · ${preset.px}px`}
                data-testid={`text-size-${preset.label.toLowerCase()}`}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      )}
      <div className="ml-3 flex items-center gap-1 border-l border-zinc-200 pl-3">
        <button
          type="button"
          className="inline-flex h-7 px-2 items-center justify-center gap-1 rounded-sm border border-transparent text-[11px] font-medium text-zinc-600 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40"
          onClick={onUndo}
          disabled={!canUndo}
          title="Undo (Cmd+Z / Ctrl+Z)"
          data-testid="undo"
        >
          Undo
        </button>
        <button
          type="button"
          className="inline-flex h-7 px-2 items-center justify-center gap-1 rounded-sm border border-transparent text-[11px] font-medium text-zinc-600 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40"
          onClick={onRedo}
          disabled={!canRedo}
          title="Redo (Cmd+Shift+Z / Ctrl+Y)"
          data-testid="redo"
        >
          Redo
        </button>
      </div>
      <div className="ml-2 text-[11px] text-zinc-400" data-testid="canvas-toolbar-hint">
        {activeTool === 'arrow'
          ? 'Click start, then click end to draw an arrow.'
          : activeTool === 'curved_arrow'
            ? 'Click numeral end, then click element end — bezier curves automatically.'
            : activeTool === 'text'
              ? 'Click to add a text annotation.'
              : activeTool === 'lasso'
                ? 'Drag a rectangle to multi-select primitives.'
                  : activeTool === 'cursor'
                    ? 'Drag a chip to move it.'
                    : `${TOOLS.find((t) => t.id === activeTool)?.label}.`}
      </div>
    </div>
  );
}

// ─── Surface (raster + SVG overlay) ──────────────────────────────

interface SurfaceProps {
  matterId: string;
  figure: DrawingFigureSummary | null;
  primitives: CanvasPrimitive[];
  numerals: DrawingNumeralRow[];
  activeTool: DrawingTool;
  zoomLevel: number;
  onMovePrimitive: (p: CanvasPrimitive, x: number, y: number) => void;
  /** BUG-019d — generic field update used by the curved-arrow
   * endpoint/control-point handles. The caller merges ``partial``
   * into the existing primitive_data_json and persists. */
  onUpdatePrimitiveData?: (
    p: CanvasPrimitive, partial: Record<string, number>,
  ) => void;
  /** Fires when the drafter clicks empty surface area while a
   * placement-style tool is active (numeral / arrow / text). */
  onCanvasClick: (coords: { x: number; y: number }) => void;
  /** Slice 6G.2 — a glossary term was dropped onto the canvas. The
   * caller creates a `text` primitive at the drop coords with the
   * term's name as the rendered text. */
  onTermDrop: (coords: { x: number; y: number }, term: DraggedTermPayload) => void;
  /** PD-005 — a registry numeral row was dropped onto the canvas.
   * The caller creates a ``numeral`` primitive bound to the
   * registry id at the drop coords. Only fires for the dedicated
   * DRAWING_NUMERAL_DRAG_TYPE mime so the term-drag path stays
   * uncontested. */
  onNumeralDrop: (
    coords: { x: number; y: number },
    payload: DraggedNumeralPayload,
  ) => void;
  /** Slice 6F arrow tool: when set, the surface renders a preview
   * line from `arrowStart` to the cursor as the drafter is choosing
   * the second point. */
  arrowStart: { x: number; y: number } | null;
  /** BUG-019d — first-click anchor for the curved-arrow tool;
   * second click commits the bezier primitive. Same shape as
   * `arrowStart` so the surface can render a marker preview. */
  curvedArrowStart: { x: number; y: number } | null;
  /** Slice 6F text tool: an inline `<input>` renders at the click
   * point until the drafter commits or cancels. */
  pendingText: { x: number; y: number } | null;
  pendingTextValue: string;
  /** BUG-019b / BUG-029 — preview the inline editor at the same px
   * size the committed primitive will render at. Falls back to 16
   * (the M preset) when the surface is rendered without a font-
   * size selection (e.g. unit tests). */
  pendingTextFontSize?: number;
  onPendingTextChange: (value: string) => void;
  onCommitPendingText: () => void;
  onCancelPendingText: () => void;
  /** Slice 6G — chip id whose chip should pulse briefly to draw the
   * drafter's attention (fired by the Numerals-tab scroll request). */
  focusedChipId: string | null;
  /** BUG-027 — fires when the raster image's natural dimensions
   * are known. Caller uses it to drive the auto-fit-on-load
   * behaviour (zoom + centre-scroll). */
  onIntrinsicSize?: (w: number, h: number) => void;
  /** BUG-032 — when true and the figure is vectorized, swap the
   * raster + overlay for the inline SVG editor. Toggled from the
   * toolbar; only takeable when ``figure.is_vectorized``. */
  vectorView?: boolean;
}

function Surface({
  matterId, figure, primitives, numerals, activeTool, zoomLevel,
  onMovePrimitive, onUpdatePrimitiveData, onCanvasClick,
  arrowStart, curvedArrowStart,
  pendingText, pendingTextValue, pendingTextFontSize,
  onPendingTextChange, onCommitPendingText,
  onCancelPendingText, focusedChipId, onTermDrop, onNumeralDrop,
  onIntrinsicSize,
  vectorView,
}: SurfaceProps) {
  if (!figure) {
    return (
      <div
        className="flex h-full items-center justify-center bg-zinc-100"
        data-testid="canvas-surface-empty"
      >
        <div className="max-w-sm px-6 text-center">
          <ImageIcon className="mx-auto mb-3 h-10 w-10 text-zinc-300" aria-hidden />
          <h3 className="mb-1 text-sm font-semibold text-zinc-700">No figure selected</h3>
          <p className="text-xs text-zinc-500">
            Pick a figure from the sidebar to view it.
          </p>
        </div>
      </div>
    );
  }

  // BUG-031 — pan layer (parent) handles position via transform;
  // Surface just renders the natural-size figure frame.
  // BUG-032 — when vectorView is on AND the figure has a traced
  // SVG, swap RasterWithOverlay for the inline VectorEditSurface
  // so drafters can click + delete stray paths. Numeral / arrow
  // / text overlays are intentionally hidden in vector mode —
  // this slice is a focused trace-edit affordance, not a unified
  // raster+vector compositor (deferred behind drafter feedback).
  if (vectorView && figure?.is_vectorized) {
    return (
      <div data-testid="canvas-surface-vector">
        <VectorEditSurface
          matterId={matterId}
          figure={figure}
          zoomLevel={zoomLevel}
        />
      </div>
    );
  }
  return (
    <div data-testid="canvas-surface">
      <RasterWithOverlay
        matterId={matterId}
        figure={figure}
        primitives={primitives}
        numerals={numerals}
        activeTool={activeTool}
        zoomLevel={zoomLevel}
        onMovePrimitive={onMovePrimitive}
        onUpdatePrimitiveData={onUpdatePrimitiveData}
        onCanvasClick={onCanvasClick}
        arrowStart={arrowStart}
        curvedArrowStart={curvedArrowStart}
        pendingText={pendingText}
        pendingTextValue={pendingTextValue}
        pendingTextFontSize={pendingTextFontSize}
        onPendingTextChange={onPendingTextChange}
        onCommitPendingText={onCommitPendingText}
        onCancelPendingText={onCancelPendingText}
        focusedChipId={focusedChipId}
        onTermDrop={onTermDrop}
        onNumeralDrop={onNumeralDrop}
        onIntrinsicSize={onIntrinsicSize}
      />
    </div>
  );
}

/**
 * Fetch the figure's traced SVG bytes when ``enabled`` is true.
 * Keyed on ``figureId + cacheBust`` so a vector-edit PATCH that
 * bumps the store's ``svgRefreshByFigure`` token re-pulls fresh
 * bytes. Returns '' until the first successful fetch; '' is also
 * the no-vectorisation / fetch-failed sentinel — callers fall
 * back to the raster PNG. Sanitisation happens at render time.
 */
function useFigureSvgContent(
  matterId: string,
  figureId: string | null,
  cacheBust: number,
  enabled: boolean,
): string {
  const [svg, setSvg] = useState<string>('');
  useEffect(() => {
    if (!enabled || !figureId) {
      setSvg('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const text = await getDrawingFigureSvg(
          matterId, figureId, cacheBust,
        );
        if (!cancelled) setSvg(text);
      } catch (exc) {
        // Soft-fail: caller renders the PNG instead. Surface to
        // DevTools so the regression isn't invisible.
        // eslint-disable-next-line no-console
        console.warn(
          '[canvas] preview-SVG fetch failed; falling back to '
          + 'raster PNG:', exc,
        );
        if (!cancelled) setSvg('');
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, matterId, figureId, cacheBust]);
  return svg;
}


/** SVG base layer for Preview mode. Wraps the sanitised SVG
 *  bytes in a sized div and flips the parent's ``imageLoaded``
 *  flag synchronously after mount so the "Image unavailable"
 *  placeholder doesn't linger behind the SVG. */
function SvgPreviewLayer({
  html, renderW, renderH, onLoaded,
}: {
  html: string;
  renderW: number;
  renderH: number;
  onLoaded: () => void;
}) {
  useEffect(() => {
    onLoaded();
  }, [html, onLoaded]);
  return (
    <div
      className="absolute left-0 top-0 [&>svg]:h-full [&>svg]:w-full"
      style={{ width: renderW, height: renderH }}
      data-testid="canvas-raster-svg"
      // sanitised by DOMPurify upstream — safe to inject.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}


function RasterWithOverlay({
  matterId, figure, primitives, numerals, activeTool, zoomLevel,
  onMovePrimitive, onUpdatePrimitiveData, onCanvasClick,
  arrowStart, curvedArrowStart,
  pendingText, pendingTextValue, pendingTextFontSize,
  onPendingTextChange, onCommitPendingText,
  onCancelPendingText, focusedChipId, onTermDrop, onNumeralDrop,
  onIntrinsicSize,
}: SurfaceProps) {
  // Preview-mode SVG fetch. When the figure has a traced SVG
  // (is_vectorized=true), render that SVG inline as the base
  // layer instead of the cached raster PNG so any vector-mode
  // edits (additions AND deletions) are reflected immediately
  // — Preview matches what export will produce.
  // Non-vectorised figures (raw IDF imports, AI-generated
  // figures awaiting vectorisation) keep using the PNG path.
  // Listens on the store's svgRefreshByFigure counter so a
  // vector-edit PATCH (PATCH /figures/{id}/svg) re-pulls fresh
  // SVG bytes the next time Preview mounts.
  const svgRefreshKey = useDrawingStore(
    (s) => (figure?.id ? s.svgRefreshByFigure[figure.id] ?? 0 : 0),
  );
  const previewSvgContent = useFigureSvgContent(
    matterId,
    figure?.id ?? null,
    svgRefreshKey,
    !!figure?.is_vectorized,
  );
  // DOMPurify pass on the SVG bytes before they hit
  // dangerouslySetInnerHTML. Mirrors KonvaCanvas:746 — same
  // profile (svg + svgFilters) so legitimate primitives survive
  // while script / on* handlers get stripped.
  const previewSvgSanitised = useMemo(
    () => (previewSvgContent
      ? DOMPurify.sanitize(previewSvgContent, {
        USE_PROFILES: { svg: true, svgFilters: true },
      }) as unknown as string
      : ''),
    [previewSvgContent],
  );
  // Cue Part 2 — selection now lives in the store. Keeping just
  // the drag-state ref here for the chip-drag (cursor tool) +
  // bezier-handle interactions.
  const selectedPrimitiveIds = useDrawingStore(
    (s) => s.selectedPrimitiveIds,
  );
  const setSelectedIds = useDrawingStore((s) => s.setSelectedIds);
  const clearSelection = useDrawingStore((s) => s.clearSelection);
  const deletePrimitives = useDrawingStore((s) => s.deletePrimitives);
  const selectedSet = useMemo(
    () => new Set(selectedPrimitiveIds),
    [selectedPrimitiveIds],
  );

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  // FT-001 (A1) — figure switch canvas flash. Without this reset
  // the previous figure's raster stays painted (browser default
  // <img> behaviour) until the new src finishes downloading, and
  // the drafter sees the wrong picture for a beat. Forcing
  // ``imageLoaded`` back to false the instant ``figure.id`` (or the
  // cache-bust token from FT-004) changes makes the surface fade
  // through the placeholder, then back up to the new figure.
  useEffect(() => {
    setImageLoaded(false);
  }, [figure?.id, figure?.updated_at]);
  const [intrinsicSize, setIntrinsicSize] = useState<{ w: number; h: number } | null>(null);
  const overlayRef = useRef<SVGSVGElement | null>(null);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);
  // BUG-019d — bezier endpoint/control-point drag. Set when the
  // drafter grabs one of the three handles a selected curved arrow
  // exposes. ``field`` chooses which (x,y) pair on the primitive
  // gets updated by pointermove.
  const [bezierHandleDrag, setBezierHandleDrag] = useState<
    { primitiveId: string; field: 'start' | 'end' | 'control' } | null
  >(null);
  // Cue Part 3 — lasso state. dragStart/dragEnd are in canvas-space
  // coords (post-zoom, post-pan). isDragging is true between the
  // pointerdown that opened the rect and the pointerup that
  // committed it. Kept local because lasso state has no consumer
  // outside this surface.
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragEnd, setDragEnd] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // FT-004 (A4) — raster cache-bust. The backend serves raster bytes
  // from disk; the browser otherwise caches them indefinitely and
  // shows the pre-Re-vectorize PNG until a hard refresh. Appending a
  // ``?_v=<updated_at-epoch>`` token forces a fresh fetch the moment
  // the figure row's updated_at bumps (Re-vectorize, trace, B/W
  // export, source-stale sync — every mutation path bumps it). We
  // fall back to the figure id when updated_at is missing so the URL
  // remains stable across renders rather than thrashing the cache.
  const rasterUrl = figure
    ? `/api/matter/${matterId}/drawing/figures/${figure.id}/raster?_v=${
        figure.updated_at ? Date.parse(figure.updated_at) || 0 : 0
      }`
    : '';

  // Surface dimensions: prefer the persisted width/height on the
  // row (Phase 5 stores them), fall back to the natural image size
  // once it loads, with a sane fallback for figures that haven't
  // landed bytes yet (broken-image case).
  const intrinsicW = figure?.width || intrinsicSize?.w || 800;
  const intrinsicH = figure?.height || intrinsicSize?.h || 600;
  const renderW = intrinsicW * zoomLevel;
  const renderH = intrinsicH * zoomLevel;

  const _toCanvasCoords = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      if (!overlayRef.current) return null;
      const rect = overlayRef.current.getBoundingClientRect();
      return {
        x: (clientX - rect.left) / zoomLevel,
        y: (clientY - rect.top) / zoomLevel,
      };
    }, [zoomLevel],
  );

  const beginCanvasMutationGroup = useDrawingStore((s) => s.beginCanvasMutationGroup);
  const endCanvasMutationGroup = useDrawingStore((s) => s.endCanvasMutationGroup);
  const showToast = useDrawingStore((s) => s.showToast);

  // Cue Part 4 — bulk delete. Reads selection from the store; the
  // floating chrome + the Delete/Backspace key both call this.
  // Frozen figures are read-only; surface a toast so the drafter
  // sees why nothing happened.
  //
  // FIX-DELETE — selection is read fresh from the store at call
  // time (via useDrawingStore.getState()) rather than closed over,
  // so the callback's identity stays stable across selection
  // changes. The keydown effect below depends on this stability.
  const onDeleteSelection = useCallback(() => {
    if (!figure) return;
    const ids = useDrawingStore.getState().selectedPrimitiveIds;
    if (ids.length === 0) return;
    if (figure.is_frozen) {
      showToast('Figure is frozen — click Unfreeze before deleting primitives.');
      return;
    }
    deletePrimitives(matterId, figure.id, ids);
  }, [figure, deletePrimitives, matterId, showToast]);

  // Window-scoped Delete / Backspace. Window-level (not div onKeyDown)
  // because clicking a chip doesn't focus the parent <div tabIndex={0}>,
  // so a div-scoped listener would silently miss the key. Skip when
  // the user is typing in an input/textarea so the registry editor and
  // pending-text input keep working.
  //
  // FIX-LASSO/DELETE — read selection through a ref. Closing over
  // selectedPrimitiveIds directly forced the effect to re-register
  // on every selection change; in the gap between cleanup + reattach
  // a keydown could land on no listener. The ref version registers
  // once per (figure, callback) pair and reads the current selection
  // every keystroke.
  const selectedIdsRef = useRef<string[]>([]);
  useEffect(() => {
    selectedIdsRef.current = selectedPrimitiveIds;
  }, [selectedPrimitiveIds]);
  useEffect(() => {
    if (!figure) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const ids = selectedIdsRef.current;
      if (ids.length === 0) return;
      const target = e.target as HTMLElement | null;
      if (target && (
        target.tagName === 'INPUT'
        || target.tagName === 'TEXTAREA'
        || target.isContentEditable
      )) return;
      e.preventDefault();
      onDeleteSelection();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [figure, onDeleteSelection]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<SVGGElement>, primitive: CanvasPrimitive) => {
      if (activeTool !== 'cursor' || !figure) return;
      e.stopPropagation();
      // Cue Part 3 — clicking a primitive selects just that one
      // (single-click select); a subsequent drag repositions it.
      setSelectedIds([primitive.id]);
      setDraggingId(primitive.id);
      // Snapshot ONCE for the whole drag — pointermove updates skip
      // per-event snapshots so undo restores the pre-drag position
      // in one step (brief §7).
      beginCanvasMutationGroup(matterId, figure.id);
      const coords = _toCanvasCoords(e.clientX, e.clientY);
      if (coords) {
        const px = (primitive.primitive_data_json as { x?: number }).x ?? 0;
        const py = (primitive.primitive_data_json as { y?: number }).y ?? 0;
        dragOffsetRef.current = { dx: coords.x - px, dy: coords.y - py };
      }
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    }, [activeTool, _toCanvasCoords, beginCanvasMutationGroup, figure, matterId, setSelectedIds],
  );

  const onPointerMove = useCallback(
    // FIX-B — handler is bound to the SVG element (not a <g>), so
    // the precise React type is SVGSVGElement. The previous
    // SVGGElement typing was a copy-paste from chip pointerdown
    // handlers and silently coerced at runtime.
    (e: React.PointerEvent<SVGSVGElement>) => {
      // Cue Part 3 — lasso drag tracks the second corner.
      if (isDragging && dragStart) {
        const c = _toCanvasCoords(e.clientX, e.clientY);
        if (c) setDragEnd(c);
        return;
      }
      // BUG-019d — bezier handle drag (start / end / control).
      // Updates a single field-pair on the curved_arrow primitive
      // so the path re-renders live as the drafter drags.
      if (bezierHandleDrag) {
        const coords = _toCanvasCoords(e.clientX, e.clientY);
        if (!coords) return;
        const p = primitives.find((q) => q.id === bezierHandleDrag.primitiveId);
        if (!p || !onUpdatePrimitiveData) return;
        const fieldMap = {
          start:   { kx: 'x1', ky: 'y1' },
          end:     { kx: 'x2', ky: 'y2' },
          control: { kx: 'cx', ky: 'cy' },
        }[bezierHandleDrag.field];
        onUpdatePrimitiveData(p, {
          [fieldMap.kx]: coords.x,
          [fieldMap.ky]: coords.y,
        });
        return;
      }
      if (!draggingId) return;
      const coords = _toCanvasCoords(e.clientX, e.clientY);
      if (!coords) return;
      const offset = dragOffsetRef.current || { dx: 0, dy: 0 };
      const x = coords.x - offset.dx;
      const y = coords.y - offset.dy;
      const p = primitives.find((q) => q.id === draggingId);
      if (!p) return;
      onMovePrimitive(p, x, y);
    }, [draggingId, _toCanvasCoords, primitives, onMovePrimitive,
        onUpdatePrimitiveData, isDragging, dragStart, bezierHandleDrag],
  );

  const onPointerUp = useCallback(
    // FIX-B — same type fix as onPointerMove above; bound to the
    // SVG element, not a <g>.
    (e: React.PointerEvent<SVGSVGElement>) => {
      // Cue Part 3 — commit the lasso. Compute the rect in canvas
      // coords, only commit when the drag is meaningful (>5px in
      // either axis), AABB-overlap-test every primitive, push the
      // matched ids to the store. Bare clicks (zero-area drag) fall
      // through and clear the selection — the surface pointerdown
      // already cleared it on drag start; this ensures consistency
      // when a click ends without movement.
      if (isDragging && dragStart && dragEnd) {
        const rect = {
          minX: Math.min(dragStart.x, dragEnd.x),
          maxX: Math.max(dragStart.x, dragEnd.x),
          minY: Math.min(dragStart.y, dragEnd.y),
          maxY: Math.max(dragStart.y, dragEnd.y),
        };
        const w = rect.maxX - rect.minX;
        const h = rect.maxY - rect.minY;
        if (w > 5 || h > 5) {
          const matched: string[] = [];
          for (const p of primitives) {
            const bbox = primitiveBoundingBox(p);
            if (!bbox) continue;
            const overlaps =
              bbox.minX <= rect.maxX && bbox.maxX >= rect.minX &&
              bbox.minY <= rect.maxY && bbox.maxY >= rect.minY;
            if (overlaps) matched.push(p.id);
          }
          setSelectedIds(matched);
        }
        setIsDragging(false);
        setDragStart(null);
        setDragEnd(null);
        (e.currentTarget as Element).releasePointerCapture?.(e.pointerId);
        return;
      }
      setDraggingId(null);
      dragOffsetRef.current = null;
      // BUG-019d — release the bezier handle when the drafter
      // lets go. Same mutation group close-out as the chip drag.
      if (bezierHandleDrag) setBezierHandleDrag(null);
      endCanvasMutationGroup();
      (e.currentTarget as Element).releasePointerCapture?.(e.pointerId);
    }, [endCanvasMutationGroup, isDragging, dragStart, dragEnd,
        primitives, setSelectedIds, bezierHandleDrag],
  );

  // Cue Part 3 — pointerdown on the SVG empty area starts a lasso
  // rectangle. Active for cursor + lasso tools alike (single-click
  // unifies the cursor's "select primitive" + the lasso's "select
  // many"). Chip pointerdown handlers stopPropagation, so this only
  // fires when the click lands on background. While in placement
  // mode, suppress lasso so the placement-click handler upstream
  // (Surface.onClick → onCanvasClick) gets the gesture.
  const onSurfacePointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (activeTool !== 'cursor' && activeTool !== 'lasso') return;
      const c = _toCanvasCoords(e.clientX, e.clientY);
      if (!c) return;
      // stopPropagation so the viewport-level pan handler (which
      // also fires on cursor-tool pointerdowns) doesn't start a
      // pan in parallel with the lasso. Pan still works on the
      // empty area outside the figure frame because that area
      // never reaches this SVG handler.
      e.stopPropagation();
      // Empty-canvas click clears the prior selection immediately;
      // a meaningful drag will then populate a new selection on
      // pointerup, while a tiny click leaves the selection empty.
      clearSelection();
      setDragStart(c);
      setDragEnd(c);
      setIsDragging(true);
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    }, [activeTool, _toCanvasCoords, clearSelection],
  );

  const [dropHover, setDropHover] = useState(false);
  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // PD-005 — accept BOTH the term-glossary drag and the new
    // registry-numeral drag. They share a hover affordance (blue
    // ring) but the drop handler discriminates by mime to route
    // each to its own onXDrop callback.
    const types = e.dataTransfer.types;
    if (
      types.includes(DRAWING_TERM_DRAG_TYPE)
      || types.includes(DRAWING_NUMERAL_DRAG_TYPE)
    ) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setDropHover(true);
    }
  }, []);
  const onDragLeave = useCallback(() => setDropHover(false), []);
  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    setDropHover(false);
    // PD-005 — try the registry-numeral mime first; the chip
    // drop is the more specific path. Term drag falls through to
    // the legacy text-primitive flow when neither numeral payload
    // is present nor parseable.
    const numeralRaw = e.dataTransfer.getData(DRAWING_NUMERAL_DRAG_TYPE);
    if (numeralRaw) {
      e.preventDefault();
      let payload: DraggedNumeralPayload | null = null;
      try {
        payload = JSON.parse(numeralRaw) as DraggedNumeralPayload;
      } catch {
        return;
      }
      if (
        !payload
        || typeof payload.numeralId !== 'string'
        || !payload.numeralId.trim()
      ) {
        return;
      }
      const coords = _toCanvasCoords(e.clientX, e.clientY);
      if (!coords) return;
      onNumeralDrop(coords, payload);
      return;
    }
    const raw = e.dataTransfer.getData(DRAWING_TERM_DRAG_TYPE);
    if (!raw) return;
    e.preventDefault();
    let payload: DraggedTermPayload | null = null;
    try {
      payload = JSON.parse(raw) as DraggedTermPayload;
    } catch {
      return;
    }
    if (!payload || typeof payload.name !== 'string' || !payload.name.trim()) return;
    const coords = _toCanvasCoords(e.clientX, e.clientY);
    if (!coords) return;
    onTermDrop(coords, payload);
  }, [_toCanvasCoords, onTermDrop, onNumeralDrop]);

  return (
    <div
      className={[
        // BUG-031 — overflow-hidden (was overflow-auto) so the frame
        // doesn't introduce its own scrollbar; the parent pan layer
        // handles positioning. The natural-size frame sits at its
        // own (renderW, renderH) and the pan layer translates it.
        'relative overflow-hidden rounded-sm border bg-white shadow-sm',
        dropHover ? 'border-[#002FA7] ring-2 ring-[#002FA7]/40' : 'border-zinc-300',
      ].join(' ')}
      style={{ width: renderW, height: renderH }}
      data-testid="canvas-raster-frame"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {previewSvgSanitised ? (
        // Preview-mode SVG render — replaces the stale-PNG path
        // for vectorised figures. The inline SVG reflects every
        // vector-mode edit (additions AND deletions) as soon as
        // PATCH /figures/{id}/svg flushes and bumps the store's
        // svgRefreshByFigure counter. The wrapped div constrains
        // the SVG to the same renderW × renderH frame the <img>
        // sized so the numeral / chip overlay below lines up
        // with the figure body (the SVG's own viewBox preserves
        // aspect via [&>svg]:h-full / w-full Tailwind escapes).
        <SvgPreviewLayer
          html={previewSvgSanitised}
          renderW={renderW}
          renderH={renderH}
          onLoaded={() => setImageLoaded(true)}
        />
      ) : (
        <img
          src={rasterUrl}
          alt={figure?.fig_label || 'Figure raster'}
          className="absolute left-0 top-0 select-none transition-opacity duration-150"
          style={{
            width: renderW,
            height: renderH,
            // FT-001 (A1) — opacity fade while the new raster is in
            // flight. Pairs with the imageLoaded reset above.
            opacity: imageLoaded ? 1 : 0,
          }}
          draggable={false}
          onLoad={(e) => {
            const img = e.target as HTMLImageElement;
            const w = img.naturalWidth;
            const h = img.naturalHeight;
            setIntrinsicSize({ w, h });
            setImageLoaded(true);
            // BUG-027 — bubble the intrinsic size up so the parent
            // can run its auto-fit / centre logic. Prefer the
            // figure-row width/height when present (Phase 5 stores
            // them); fall back to the natural pixels for figures
            // imported before that field landed.
            const reportW = figure?.width || w;
            const reportH = figure?.height || h;
            onIntrinsicSize?.(reportW, reportH);
          }}
          onError={() => setImageLoaded(false)}
        />
      )}
      {!imageLoaded && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-zinc-50 text-xs text-zinc-400"
          aria-hidden
        >
          {/* Raster either still loading or 404 (Phase 5 hasn't
              landed bytes yet for this figure). The overlay still
              works either way. */}
          <ImageIcon className="mr-2 h-5 w-5" />
          Image unavailable
        </div>
      )}
      <svg
        ref={overlayRef}
        className={[
          'absolute left-0 top-0',
          // Click-to-place tools turn the cursor into a crosshair so
          // the drafter sees the canvas is in placement mode.
          (activeTool === 'arrow'
            || activeTool === 'curved_arrow' || activeTool === 'text'
            || activeTool === 'lasso')
            ? 'cursor-crosshair'
            : '',
        ].join(' ')}
        width={renderW}
        height={renderH}
        viewBox={`0 0 ${intrinsicW} ${intrinsicH}`}
        preserveAspectRatio="xMidYMid meet"
        onPointerDown={onSurfacePointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onClick={(e) => {
          // Ignore clicks that originated on a primitive (those have
          // their own pointerdown handlers and we don't want a stray
          // click-through to fire a placement at the same coords).
          // The chip group element calls e.stopPropagation, so this
          // handler only sees clicks on the empty overlay.
          if (activeTool === 'cursor' || activeTool === 'zoom') return;
          // Lasso clicks are consumed by the drag-rectangle path; a
          // bare click without movement is a no-op.
          if (activeTool === 'lasso') return;
          const coords = _toCanvasCoords(e.clientX, e.clientY);
          if (coords) onCanvasClick(coords);
        }}
        data-testid="canvas-overlay"
      >
        <defs>
          <marker
            id="arrowhead"
            viewBox="0 0 10 10"
            refX="9" refY="5"
            markerWidth="6" markerHeight="6"
            orient="auto-start-reverse"
          >
            {/* BUG-019a — black arrowhead for drawn primitives so
                the rendered figure matches USPTO/EPO patent-drawing
                conventions. The selection / hover highlight stays
                blue elsewhere. */}
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#000000" />
          </marker>
          <pattern
            id="staleHatch" width="6" height="6"
            patternUnits="userSpaceOnUse" patternTransform="rotate(45)"
          >
            <line x1="0" y1="0" x2="0" y2="6" stroke="#71717a" strokeWidth="2" />
          </pattern>
          <style>{`
            @keyframes drawing-chip-pulse {
              0%   { r: 22; opacity: 0.9; }
              100% { r: 36; opacity: 0;   }
            }
          `}</style>
        </defs>
        {primitives.map((p) => renderPrimitive(p, {
          numerals,
          selected: selectedSet.has(p.id),
          focused: p.id === focusedChipId,
          onPointerDown,
          cursorToolActive: activeTool === 'cursor',
          setSelectedId: (id) => setSelectedIds(id ? [id] : []),
          onBezierHandleDown: (e, primitiveId, field) => {
            if (activeTool !== 'cursor' || !figure) return;
            e.stopPropagation();
            // Mirror the chip-drag mutation group so a handle drag
            // is undone in one step rather than per-pointermove.
            beginCanvasMutationGroup(matterId, figure.id);
            setBezierHandleDrag({ primitiveId, field });
            (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
          },
        }))}
        {/* Arrow tool preview — renders the line as the drafter
            chooses the end point. The end coords trail the cursor
            via a small overlay handler below. */}
        {arrowStart && (
          <circle
            cx={arrowStart.x} cy={arrowStart.y} r={4}
            fill="#002FA7" stroke="#ffffff" strokeWidth="2"
            data-testid="arrow-start-preview"
          />
        )}
        {curvedArrowStart && (
          <circle
            cx={curvedArrowStart.x} cy={curvedArrowStart.y} r={4}
            fill="#16a34a" stroke="#ffffff" strokeWidth="2"
            data-testid="curved-arrow-start-preview"
          />
        )}
        {/* Cue Part 3 — live lasso rectangle. Stroke + dasharray
            scaled by 1/zoom so the dashes look the same physical
            size at every zoom level. */}
        {isDragging && dragStart && dragEnd && (
          <rect
            x={Math.min(dragStart.x, dragEnd.x)}
            y={Math.min(dragStart.y, dragEnd.y)}
            width={Math.abs(dragEnd.x - dragStart.x)}
            height={Math.abs(dragEnd.y - dragStart.y)}
            fill="rgba(37, 99, 235, 0.10)"
            stroke="#2563eb"
            strokeWidth={1 / zoomLevel}
            strokeDasharray={`${4 / zoomLevel} ${2 / zoomLevel}`}
            pointerEvents="none"
            data-testid="lasso-drag-rect"
          />
        )}
      </svg>
      {/* Cue Part 4 — floating delete chrome. Renders top-right of
          the figure frame whenever any primitive is selected; the
          red affordance is discoverable, the Del/Backspace key does
          the same. Hidden on frozen figures so the drafter sees
          the Unfreeze flow first. */}
      {selectedPrimitiveIds.length > 0 && figure && !figure.is_frozen && (
        <div
          className="absolute right-2 top-2 z-10 flex items-center gap-2 rounded-sm bg-red-500 px-3 py-1 text-white shadow-md"
          data-testid="canvas-selection-actions"
        >
          <span className="text-[12px]">
            🗑 Delete {selectedPrimitiveIds.length} selected
          </span>
          <button
            type="button"
            className="inline-flex h-6 items-center gap-1 rounded-sm bg-white/20 px-2 text-[11px] font-medium hover:bg-white/30"
            onClick={onDeleteSelection}
            title="Delete selected (Del / Backspace)"
            data-testid="canvas-delete-selection"
          >
            Delete
          </button>
        </div>
      )}
      {/* Inline text input rendered as an HTML overlay (SVG <text>
          isn't editable). Sized + positioned in screen space — the
          coords are scaled by zoomLevel to match the underlying
          intrinsic-coord SVG. */}
      {pendingText && (
        <input
          type="text"
          autoFocus
          value={pendingTextValue}
          onChange={(e) => onPendingTextChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onCommitPendingText();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onCancelPendingText();
            }
          }}
          onBlur={() => {
            // Commit on blur so click-away doesn't lose input.
            if (pendingTextValue.trim()) onCommitPendingText();
            else onCancelPendingText();
          }}
          className="absolute rounded-sm border border-[#002FA7] bg-white px-1 py-0.5 shadow-sm focus:outline-none"
          style={{
            left: pendingText.x * zoomLevel,
            top: pendingText.y * zoomLevel - (pendingTextFontSize ?? 16) * 0.75,
            minWidth: 120,
            // BUG-019b — preview matches the committed primitive size
            // so drafters get WYSIWYG feedback while typing. The
            // ``zoomLevel`` multiplier mirrors the SVG <text>
            // rendering path which is itself scaled by zoom.
            fontSize: (pendingTextFontSize ?? 16) * zoomLevel,
          }}
          placeholder="Annotation…"
          data-testid="pending-text-input"
        />
      )}
    </div>
  );
}

interface PrimitiveRenderCtx {
  numerals: DrawingNumeralRow[];
  selected: boolean;
  /** Slice 6G — chip is the active focus target from the Numerals
   * tab; renders a transient pulse ring. */
  focused: boolean;
  onPointerDown: (e: React.PointerEvent<SVGGElement>, p: CanvasPrimitive) => void;
  /** BUG-019d — engaged when the cursor tool is active so curved
   * arrows are clickable to select; ``setSelectedId`` flips the
   * selection from outside the primitive component. */
  cursorToolActive?: boolean;
  setSelectedId?: (id: string | null) => void;
  /** BUG-019d — curved-arrow handle grab. Pushes drag state into
   * the surface so subsequent pointermove updates the right
   * (x,y) pair. */
  onBezierHandleDown?: (
    e: React.PointerEvent<SVGCircleElement>,
    primitiveId: string,
    field: 'start' | 'end' | 'control',
  ) => void;
}

function renderPrimitive(p: CanvasPrimitive, ctx: PrimitiveRenderCtx): React.ReactNode {
  // NB-09 — the numeral is printed in the image; no chip to draw.
  if (isRasterAnchor(p)) return null;
  switch (p.primitive_type) {
    case 'numeral':       return <NumeralChip key={p.id} primitive={p} {...ctx} />;
    case 'arrow':         return <ArrowPrimitive key={p.id} primitive={p} />;
    case 'curved_arrow':  return <CurvedArrowPrimitive key={p.id} primitive={p} {...ctx} />;
    case 'text':          return <TextPrimitive key={p.id} primitive={p} />;
    case 'lasso':         return <LassoPrimitive key={p.id} primitive={p} />;
    default:              return null;
  }
}

// ─── Numeral chip with the four states ───────────────────────────

function NumeralChip({
  primitive, numerals, selected, focused, onPointerDown,
}: {
  primitive: CanvasPrimitive;
} & PrimitiveRenderCtx) {
  const groupRef = React.useRef<SVGGElement | null>(null);
  // Scroll-into-view on focus request from the Numerals tab.
  React.useEffect(() => {
    if (focused && groupRef.current) {
      try {
        groupRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      } catch {
        /* legacy browser fallback — no-op */
      }
    }
  }, [focused]);
  const data = primitive.primitive_data_json as {
    x?: number; y?: number; numeral?: string; label?: string;
  };
  const x = data.x ?? 0;
  const y = data.y ?? 0;
  // Read label + binding from the registry so a PATCH on the
  // numerals row propagates to every chip without a refetch.
  const registryRow = primitive.drawing_numeral_id
    ? numerals.find((n) => n.id === primitive.drawing_numeral_id) || null
    : null;
  const numeralText = registryRow?.numeral || data.numeral || '?';
  const isOrphan = !primitive.drawing_numeral_id
    || (registryRow && !registryRow.bound_claim_id);
  const isStale = !!registryRow?.is_stale;

  // Fill states per brief §5: normal blue / orphan amber / stale grey
  // with hatch / selected adds an outline ring.
  const fill = isStale ? 'url(#staleHatch)'
    : (isOrphan ? '#fbbf24' : '#002FA7');
  const textFill = isStale ? '#3f3f46' : '#ffffff';
  const ring = selected ? 2 : 0;

  // Tech-debt slice TD-2 — authoritative cross-figure presence
  // count from the registry. ``GET /numerals`` server-side joins
  // drawing_canvas_state and returns a per-numeral
  // ``figure_ids: string[]`` array (see backend/drawing.py:885),
  // so the chip tooltip can quote the exact count without
  // navigating to every figure first. Replaces the slice 8F.2
  // per-figure-cache lower-bound (which carried a "cached
  // locally — visit other figures to refresh count" hedge).
  // ``refreshLists()`` re-fetches the numerals list after every
  // numeral place / remove, so this stays current automatically.
  const presenceCount = registryRow?.figure_ids?.length ?? 0;

  const tooltipLines: string[] = [
    `Numeral: ${numeralText}`,
    `Label: ${registryRow?.label ?? data.label ?? '(no registry binding)'}`,
  ];
  if (registryRow?.bound_claim_id) {
    tooltipLines.push(
      `Claim term: ${registryRow.bound_claim_term || '(bound to a claim)'}`,
    );
  } else {
    tooltipLines.push('Claim term: (none — not linked to a claim)');
  }
  if (presenceCount > 0) {
    tooltipLines.push(
      `Appears on ${presenceCount} figure${presenceCount === 1 ? '' : 's'}`,
    );
  }
  if (isStale) {
    tooltipLines.push('Status: out of date — the figure changed after this numeral was checked.');
  }
  const tooltip = tooltipLines.join('\n');

  return (
    <g
      ref={groupRef}
      className="cursor-grab active:cursor-grabbing"
      transform={`translate(${x} ${y})`}
      onPointerDown={(e) => onPointerDown(e, primitive)}
      data-testid={`chip-${primitive.id}`}
      data-presence-count={presenceCount}
      data-state={isStale ? 'stale' : isOrphan ? 'warn' : 'normal'}
      data-selected={selected ? 'true' : 'false'}
      data-focused={focused ? 'true' : 'false'}
    >
      {/* Native SVG <title> renders as the browser's hover tooltip
          when the cursor rests on the chip group — slice 8F.2 hover
          contract per the brief. Multi-line via \n. */}
      <title>{tooltip}</title>
      {/* Slice 6G focus pulse — a soft outer ring fades in for
          ~1500ms when the chip is the panel's scroll target. */}
      {focused && (
        <circle
          r={28} fill="none" stroke="#002FA7" strokeWidth="3"
          opacity="0.6"
          style={{
            animation: 'drawing-chip-pulse 1.5s ease-out forwards',
          }}
        />
      )}
      <circle r={20} fill={fill} stroke={selected ? '#002FA7' : 'transparent'}
              strokeWidth={ring + 2} />
      <text
        x={0} y={0}
        dominantBaseline="central" textAnchor="middle"
        fill={textFill}
        fontSize={isOrphan && !isStale ? 13 : 14}
        fontWeight={600}
        fontFamily="ui-monospace, monospace"
      >
        {numeralText}{isOrphan && !isStale ? '?' : ''}
      </text>
    </g>
  );
}

function ArrowPrimitive({ primitive }: { primitive: CanvasPrimitive }) {
  const d = primitive.primitive_data_json as {
    x1?: number; y1?: number; x2?: number; y2?: number;
  };
  return (
    <line
      x1={d.x1 ?? 0} y1={d.y1 ?? 0}
      x2={d.x2 ?? 0} y2={d.y2 ?? 0}
      stroke="#000000" strokeWidth={1.5}
      markerEnd="url(#arrowhead)"
      data-testid={`arrow-${primitive.id}`}
    />
  );
}

function CurvedArrowPrimitive({
  primitive, selected, cursorToolActive, setSelectedId, onBezierHandleDown,
}: {
  primitive: CanvasPrimitive;
} & PrimitiveRenderCtx) {
  // BUG-019d — quadratic-bezier leader line. The control point
  // (cx, cy) defaults to a perpendicular offset from the midpoint
  // when not stored explicitly so older arrows imported before
  // this primitive type still render with a sensible curve.
  const d = primitive.primitive_data_json as {
    x1?: number; y1?: number; x2?: number; y2?: number;
    cx?: number; cy?: number;
  };
  const x1 = d.x1 ?? 0;
  const y1 = d.y1 ?? 0;
  const x2 = d.x2 ?? 0;
  const y2 = d.y2 ?? 0;
  const { cx, cy } = (d.cx != null && d.cy != null)
    ? { cx: d.cx, cy: d.cy }
    : defaultBezierControl(x1, y1, x2, y2);
  return (
    <g data-testid={`curved-arrow-${primitive.id}`}>
      {/* BUG-019d — wide invisible hit-target so the drafter can
          actually click the curve to select it. The narrow 1.5px
          stroke alone is too thin to grab reliably. Only enabled
          when the cursor tool is active so it doesn't swallow
          placement clicks. */}
      {cursorToolActive && (
        <path
          d={`M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`}
          fill="none"
          stroke="transparent"
          strokeWidth={14}
          style={{ cursor: 'pointer' }}
          onPointerDown={(e) => {
            e.stopPropagation();
            setSelectedId?.(primitive.id);
          }}
          data-testid={`curved-arrow-hit-${primitive.id}`}
        />
      )}
      <path
        d={`M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`}
        fill="none"
        stroke={selected ? '#002FA7' : '#000000'}
        strokeWidth={selected ? 2 : 1.5}
        markerEnd="url(#arrowhead)"
        pointerEvents="none"
      />
      {/* BUG-019d — three reshape handles surface only while the
          arrow is selected + the cursor tool is active. The
          start/end handles are square (endpoints), the control
          handle is round (mid). All three accept a pointerdown
          that hands the surface a (primitiveId, field) pair the
          pointermove path consumes. */}
      {selected && cursorToolActive && (
        <g data-testid={`curved-arrow-handles-${primitive.id}`}>
          <BezierHandle
            cx={x1} cy={y1} shape="square"
            onPointerDown={(e) => onBezierHandleDown?.(e, primitive.id, 'start')}
            testid={`curved-arrow-handle-start-${primitive.id}`}
          />
          <BezierHandle
            cx={x2} cy={y2} shape="square"
            onPointerDown={(e) => onBezierHandleDown?.(e, primitive.id, 'end')}
            testid={`curved-arrow-handle-end-${primitive.id}`}
          />
          <BezierHandle
            cx={cx} cy={cy} shape="circle"
            onPointerDown={(e) => onBezierHandleDown?.(e, primitive.id, 'control')}
            testid={`curved-arrow-handle-control-${primitive.id}`}
          />
        </g>
      )}
    </g>
  );
}

/** BUG-019d — small reshape handle drawn next to a selected
 * curved arrow's anchor or control point. Square = endpoint
 * (drag to reposition the tail/head); round = control point
 * (drag to reshape the curve). White-fill + blue-stroke matches
 * the existing selection accent the chip drag uses. */
function BezierHandle({
  cx, cy, shape, onPointerDown, testid,
}: {
  cx: number;
  cy: number;
  shape: 'square' | 'circle';
  onPointerDown: (e: React.PointerEvent<SVGCircleElement>) => void;
  testid: string;
}) {
  const r = 5;
  const fill = '#ffffff';
  const stroke = '#002FA7';
  const strokeWidth = 1.5;
  const style = { cursor: 'grab' as const };
  if (shape === 'square') {
    return (
      <rect
        x={cx - r} y={cy - r} width={r * 2} height={r * 2}
        fill={fill} stroke={stroke} strokeWidth={strokeWidth} style={style}
        onPointerDown={onPointerDown as unknown as React.PointerEventHandler<SVGRectElement>}
        data-testid={testid}
      />
    );
  }
  return (
    <circle
      cx={cx} cy={cy} r={r}
      fill={fill} stroke={stroke} strokeWidth={strokeWidth} style={style}
      onPointerDown={onPointerDown}
      data-testid={testid}
    />
  );
}

/** BUG-019c — coarse bounding box used for the lasso hit-test.
 * Returns null for primitive shapes whose data shape isn't
 * recognised (forwards-compat with future primitive types).
 * The numbers are intentionally generous so a chip whose label
 * extends past the circle still gets picked up. */
function primitiveBoundingBox(p: CanvasPrimitive): {
  minX: number; maxX: number; minY: number; maxY: number;
} | null {
  if (isRasterAnchor(p)) return null;
  const d = p.primitive_data_json as Record<string, unknown>;
  switch (p.primitive_type) {
    case 'numeral': {
      const x = (d.x as number) ?? 0;
      const y = (d.y as number) ?? 0;
      return { minX: x - 24, maxX: x + 24, minY: y - 24, maxY: y + 24 };
    }
    case 'arrow':
    case 'curved_arrow': {
      const x1 = (d.x1 as number) ?? 0;
      const y1 = (d.y1 as number) ?? 0;
      const x2 = (d.x2 as number) ?? 0;
      const y2 = (d.y2 as number) ?? 0;
      return {
        minX: Math.min(x1, x2), maxX: Math.max(x1, x2),
        minY: Math.min(y1, y2), maxY: Math.max(y1, y2),
      };
    }
    case 'text': {
      const x = (d.x as number) ?? 0;
      const y = (d.y as number) ?? 0;
      const text = (d.text as string) || '';
      const w = Math.max(20, text.length * 8);
      return { minX: x, maxX: x + w, minY: y - 16, maxY: y + 4 };
    }
    case 'lasso': {
      const pts = (d.points as Array<{ x: number; y: number }>) || [];
      if (pts.length === 0) return null;
      const xs = pts.map((pt) => pt.x);
      const ys = pts.map((pt) => pt.y);
      return {
        minX: Math.min(...xs), maxX: Math.max(...xs),
        minY: Math.min(...ys), maxY: Math.max(...ys),
      };
    }
    default:
      return null;
  }
}

/** Compute a curve control point ~30% perpendicular to the
 * straight line from (x1,y1) to (x2,y2). Used both at draw time
 * to seed the bezier and at render time when an older row lacks
 * explicit cx/cy. */
function defaultBezierControl(
  x1: number, y1: number, x2: number, y2: number,
): { cx: number; cy: number } {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  // Unit perpendicular (rotate +90°), scale by 30% of the line.
  const px = -dy / len;
  const py =  dx / len;
  const offset = len * 0.30;
  return { cx: mx + px * offset, cy: my + py * offset };
}

function TextPrimitive({ primitive }: { primitive: CanvasPrimitive }) {
  const d = primitive.primitive_data_json as {
    x?: number; y?: number; text?: string; fontSize?: number;
  };
  // BUG-019b — patent-drawing default 16px so annotations stay
  // readable at 100% zoom. Honours an explicit fontSize when
  // present (older rows / future per-text-overrides).
  const fontSize = d.fontSize ?? 16;
  return (
    <text
      x={d.x ?? 0} y={d.y ?? 0}
      fill="#000000" fontSize={fontSize} fontFamily="ui-sans-serif, sans-serif"
      data-testid={`text-${primitive.id}`}
    >
      {d.text || ''}
    </text>
  );
}

function LassoPrimitive({ primitive }: { primitive: CanvasPrimitive }) {
  const d = primitive.primitive_data_json as {
    points?: Array<{ x: number; y: number }>;
  };
  const points = (d.points || [])
    .map((pt) => `${pt.x},${pt.y}`).join(' ');
  return (
    <polygon
      points={points}
      fill="rgba(0, 0, 0, 0.04)"
      stroke="#000000" strokeWidth={1} strokeDasharray="6 4"
      data-testid={`lasso-${primitive.id}`}
    />
  );
}

// ─── Action bench (Phase 8 territory — still disabled) ───────────

export function ActionBench({
  matterId, figure,
}: {
  matterId: string;
  figure: DrawingFigureSummary | null;
}) {
  const disabled = !figure;
  // WISHLIST_REGENERATE_FIGURE Phase 16 — local UI state for the
  // regenerate-from-image modal. Kept local (not in the store)
  // because nothing outside this component needs to react to it;
  // the cross-component handoff happens via setGenerationJob()
  // inside the panel itself.
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const openAnalyzeModal = useDrawingStore((s) => s.openAnalyzeModal);
  const setLatestVerifyResult = useDrawingStore((s) => s.setLatestVerifyResult);
  const setVerifyingFigureId = useDrawingStore((s) => s.setVerifyingFigureId);
  const verifyingFigureId = useDrawingStore((s) => s.verifyingFigureId);
  const latestVerifyByFigure = useDrawingStore((s) => s.latestVerifyByFigure);
  const setMutationError = useDrawingStore((s) => s.setMutationError);

  const verifyResult = figure ? latestVerifyByFigure[figure.id] : undefined;
  const verifyTotal = verifyResult?.total ?? 0;
  const isVerifying = !!figure && verifyingFigureId === figure.id;
  const showToast = useDrawingStore((s) => s.showToast);
  const refreshLists = useDrawingStore((s) => s.refreshLists);
  const loadCanvasForAutoMap = useDrawingStore((s) => s.loadCanvasFor);
  const [autoMapping, setAutoMapping] = useState(false);
  const [freezing, setFreezing] = useState(false);
  const [pendingFreezeIssues, setPendingFreezeIssues] = useState<VerifyIssue[] | null>(null);

  const onClickAnalyze = useCallback(() => {
    if (figure) openAnalyzeModal(figure.id);
  }, [figure, openAnalyzeModal]);

  const onClickAutoMap = useCallback(async () => {
    if (!figure || autoMapping) return;
    setAutoMapping(true);
    try {
      const r = await apiAutoMapMatter(matterId, figure.id);
      // Refresh the figures list so the gallery card's
      // unmapped_terms_count badge updates immediately.
      await refreshLists(matterId);
      // NB-09 — the vision pass records the numerals printed in
      // the figure as on this figure (canvas rows). Re-pull the
      // canvas so the status line's numeral count, the Numerals
      // tab grouping and the "Run Auto-map" hint reflect them.
      await loadCanvasForAutoMap(matterId, figure.id, true);
      // Format the result into a single human-readable line.
      // BUG-007 — when the server emits a diagnostic ``hint``
      // (the standard counts couldn't explain the outcome — e.g.
      // total_unmapped=0 because Analyze hasn't run), surface
      // the hint instead of the generic "Nothing to auto-map."
      // copy. Drafters get the actionable next step inline.
      const bits: string[] = [];
      const totalResolved = r.auto_mapped + r.llm_resolved;
      if (totalResolved > 0) {
        bits.push(
          `${totalResolved} term${totalResolved === 1 ? '' : 's'} mapped `
          + `(${r.auto_mapped} by name match, ${r.llm_resolved} by AI)`,
        );
      }
      const stillOpen = r.ambiguous + r.low_confidence + r.llm_unresolved;
      if (stillOpen > 0) {
        bits.push(
          `${stillOpen} still need${stillOpen === 1 ? 's' : ''} review`,
        );
      }
      if (r.no_match > 0) {
        bits.push(`${r.no_match} with no candidate match`);
      }
      if (r.total_unmapped === 0 && !r.hint) {
        bits.push('Nothing to auto-map.');
      }
      if (r.hint) {
        bits.push(r.hint);
      }
      // Surface a fresh-vision-extraction count when meaningful
      // — drafters who just ran auto-map on a re-imported figure
      // want to see that the vision pass DID find numerals even
      // if nothing got bound yet (typically because Analyze
      // hasn't run).
      if ((r.vision_extracted_count ?? 0) > 0 && totalResolved === 0) {
        // NB-09 — say what actually changed. The old line claimed
        // every found numeral was "added to the registry" even when
        // all of them were already there.
        const found = r.vision_extracted_count ?? 0;
        const added = r.vision_new_count;
        const placed = r.vision_placed_count;
        let line = `Found ${found} numeral${found === 1 ? '' : 's'} on the figure`;
        if (typeof added === 'number') {
          line += added > 0
            ? ` (${added} new to the numeral list)`
            : ' (all already in the numeral list)';
        }
        if (typeof placed === 'number' && placed > 0) {
          line += `; ${placed} now shown as on this figure`;
        }
        bits.push(`${line}.`);
      }
      if (r.llm_error) {
        bits.push(`AI matching could not finish: ${r.llm_error}`);
      }
      showToast(`Auto-map: ${bits.join(' · ')}`);
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? 'Auto-map failed.';
      setMutationError(`Auto-map failed: ${msg}`);
    } finally {
      setAutoMapping(false);
    }
  }, [figure, autoMapping, matterId, refreshLists, loadCanvasForAutoMap, showToast, setMutationError]);

  const runFreeze = useCallback(async (acknowledge: boolean) => {
    if (!figure || freezing) return;
    setFreezing(true);
    try {
      const r = await apiFreezeFigure(matterId, figure.id, acknowledge);
      await refreshLists(matterId);
      setPendingFreezeIssues(null);
      const ackHint = r.issues_acknowledged
        ? ` (acknowledged ${r.issue_count} issue${r.issue_count === 1 ? '' : 's'})`
        : '';
      showToast(`Figure frozen${ackHint}.`);
    } catch (e: unknown) {
      const errResp = (e as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
      const detail = errResp?.data?.detail as
        | string
        | {
            error_code?: string;
            detail?: string;
            figure_id?: string;
            issues?: VerifyIssue[];
            counts?: VerifyFigureResponse['counts'];
            total?: number;
          }
        | undefined;
      // Hotfix: backend now returns 409 has_issues (matching the
      // rest of the freeze-gate's conflict-error pattern —
      // already_frozen / drawings_locked / figure_frozen all 409).
      // Status flipped from 400 → 409 in lockstep with the
      // backend; error_code keeps its existing wire shape.
      if (errResp?.status === 409
          && typeof detail === 'object'
          && detail?.error_code === 'has_issues'
          && Array.isArray(detail.issues)
          && figure) {
        // Open the confirm dialog with the issue list. Drafter
        // resolves via "Freeze anyway" / "Map terms first" / "Cancel".
        setPendingFreezeIssues(detail.issues);
        // ALSO mirror the (now VerifyFigureResponse-shaped) payload
        // into the verify store so the VerifyFindingsPanel below the
        // action bench lights up with the per-issue drilldown
        // alongside the dialog summary — same cache the explicit
        // Verify-button click feeds.
        if (detail.counts && typeof detail.total === 'number') {
          setLatestVerifyResult(figure.id, {
            figure_id: detail.figure_id ?? figure.id,
            issues: detail.issues,
            counts: detail.counts,
            total: detail.total,
          });
        }
      } else {
        // BUG-006 — surface a structured error_code as an
        // actionable string rather than dumping the raw inner
        // `detail` (which on `drawings_locked` reads as the
        // opaque "claims_not_frozen"). The backend already
        // returns {error_code, detail} for all three 409 paths
        // (drawings_locked / already_frozen / has_issues);
        // map the first two to drafter-readable copy here.
        let msg: string;
        if (typeof detail === 'object' && detail) {
          const code = detail.error_code;
          if (code === 'drawings_locked') {
            msg = 'Claims must be frozen before drafting figures. Return to Stage 3 and freeze the claim set first.';
          } else if (code === 'already_frozen') {
            msg = 'This figure is already frozen. Unfreeze it first if you need to re-freeze.';
          } else if (code === 'description_required') {
            // BUG-DRAW-003 — figure missing description.
            // Backend's detail string already explains what
            // to do; surface it verbatim. The Description
            // field on the right rail picks up the freshly-
            // typed value via the standard CollapsibleField
            // auto-save (no extra wiring needed here).
            msg = (typeof detail.detail === 'string' && detail.detail)
              ? detail.detail
              : 'Add a description for this figure before freezing.';
          } else if (typeof detail.detail === 'string' && detail.detail) {
            msg = detail.detail;
          } else {
            msg = 'Freeze failed.';
          }
        } else if (typeof detail === 'string' && detail) {
          msg = detail;
        } else {
          msg = (e as { message?: string })?.message ?? 'Freeze failed.';
        }
        setMutationError(`Freeze failed: ${msg}`);
      }
    } finally {
      setFreezing(false);
    }
  }, [
    figure, freezing, matterId, refreshLists, showToast,
    setMutationError, setLatestVerifyResult,
  ]);

  const onClickFreeze = useCallback(() => {
    void runFreeze(false);
  }, [runFreeze]);

  const onConfirmFreezeAnyway = useCallback(() => {
    void runFreeze(true);
  }, [runFreeze]);

  const onMapTermsFirst = useCallback(() => {
    setPendingFreezeIssues(null);
    if (figure) showToast('Click Auto-map to bind unmapped terms, then re-try Freeze.');
  }, [figure, showToast]);

  const onCancelFreeze = useCallback(() => {
    setPendingFreezeIssues(null);
  }, []);

  const onClickUnfreeze = useCallback(async () => {
    if (!figure || freezing) return;
    setFreezing(true);
    try {
      await apiUnfreezeFigure(matterId, figure.id);
      await refreshLists(matterId);
      showToast('Figure unfrozen — its analysis and canvas can be edited again.');
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? 'Unfreeze failed.';
      setMutationError(`Unfreeze failed: ${msg}`);
    } finally {
      setFreezing(false);
    }
  }, [figure, freezing, matterId, refreshLists, showToast, setMutationError]);

  const onClickVerify = useCallback(async () => {
    if (!figure || isVerifying) return;
    // Simplified click handler — the dismissed-sig dance is
    // gone with VerifyFindingsPanel. The status strip below
    // owns the result surface (count + click-to-popover);
    // the toast here just confirms the click landed.
    setVerifyingFigureId(figure.id);
    try {
      const r = await apiVerifyFigure(matterId, figure.id);
      setLatestVerifyResult(figure.id, r);
      if (r.total === 0) {
        showToast('Verify clean — no issues found.');
      } else {
        showToast(
          `Verify: ${r.total} issue${r.total === 1 ? '' : 's'} — `
          + 'click the count in the status bar to view.',
        );
      }
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? 'Verify failed.';
      setMutationError(`Verify failed: ${msg}`);
    } finally {
      setVerifyingFigureId(null);
    }
  }, [
    figure, isVerifying, matterId,
    setVerifyingFigureId, setLatestVerifyResult, setMutationError,
    showToast,
  ]);

  // BUG-032 — opt-in vectorize. The Phase 7B tracer ran only on the
  // AI-generation path; this hook surfaces it on every figure so
  // drafters can convert IDF/uploaded raster → editable SVG when
  // (and only when) they need to delete a stray path.
  const upsertFigureFromPatch = useDrawingStore((s) => s.upsertFigureFromPatch);
  const bumpSvgRefresh = useDrawingStore((s) => s.bumpSvgRefresh);
  const loadCanvasFor = useDrawingStore((s) => s.loadCanvasFor);
  const [vectorizing, setVectorizing] = useState(false);
  const onClickVectorize = useCallback(async () => {
    if (!figure || vectorizing) return;
    setVectorizing(true);
    try {
      const r = await vectorizeDrawingFigure(matterId, figure.id);
      upsertFigureFromPatch(r.figure);
      // KonvaCanvas's useFigureSvg listens on this counter (NOT
      // figure.updated_at, which canvas writes also bump). Tells
      // the vector layer the SVG bytes have changed and a re-fetch
      // is appropriate. Decouples from the chunk-3 "vector flash"
      // where every canvas autosave was triggering a refetch.
      bumpSvgRefresh(figure.id);
      // BUG-015 Phase 6 — refresh figureUpdatedAtCache. The
      // vectorize endpoint bumps the figure's updated_at server-
      // side; without this re-load the local cache still carries
      // the pre-vectorize timestamp, and the next window-focus
      // event triggers checkForCrossTabWrite's "Canvas updated in
      // another tab — refreshing" toast against the SAME tab that
      // just did the write. force=true so we always re-pull the
      // post-vectorize timestamp even when the cache has a
      // stale entry. Fire-and-forget — the toast is the bug here,
      // not data loss, so a refresh-failure is acceptable.
      void loadCanvasFor(matterId, figure.id, true);
      showToast(
        figure.is_vectorized
          ? 'Re-traced — SVG refreshed.'
          : 'Vectorized — toggle Vector view to edit.',
      );
    } catch (e: unknown) {
      const errResp = (e as { response?: { status?: number; data?: { detail?: unknown; error_code?: string } } })?.response;
      const detail = errResp?.data?.detail as string | undefined;
      const errCode = (errResp?.data as { error_code?: string } | undefined)?.error_code;
      if (errCode === 'trace_failed') {
        setMutationError(`Tracing failed: ${detail || 'unknown'}. Figure stays raster-only.`);
      } else {
        const msg = detail || (e as { message?: string })?.message || 'Vectorize failed.';
        setMutationError(`Vectorize failed: ${msg}`);
      }
    } finally {
      setVectorizing(false);
    }
  }, [
    figure, vectorizing, matterId,
    upsertFigureFromPatch, bumpSvgRefresh, loadCanvasFor,
    showToast, setMutationError,
  ]);

  return (
    <div
      className="flex items-center justify-end gap-2 border-t border-zinc-200 bg-white px-3 py-2"
      data-testid="canvas-action-bench"
    >
      <ActionButton
        icon={<Sparkles className="h-3.5 w-3.5" />}
        label="Analyze"
        disabled={disabled}
        tooltip="Read this figure against the frozen claims and suggest its reference numerals."
        testid="action-analyze"
        onClick={onClickAnalyze}
      />
      <ActionButton
        icon={
          autoMapping
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Link2 className="h-3.5 w-3.5" />
        }
        label="Auto-map"
        disabled={disabled || autoMapping}
        tooltip="Bind claim terms that have no numeral yet to matching numerals in the registry."
        testid="action-auto-map"
        onClick={onClickAutoMap}
        rightSlot={
          figure && figure.unmapped_terms_count > 0
            ? (
              <span
                className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-100 px-1 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-200 tabular-nums"
                data-testid="action-auto-map-badge"
                title={`${figure.unmapped_terms_count} unmapped claim term(s)`}
              >
                {figure.unmapped_terms_count}
              </span>
            )
            : undefined
        }
      />
      <ActionButton
        icon={
          isVerifying
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <CheckCircle2 className="h-3.5 w-3.5" />
        }
        label="Verify"
        disabled={disabled || isVerifying}
        tooltip="Check numerals against claim terms: unbound numerals, claims with no numeral, duplicate or stale labels."
        testid="action-verify"
        onClick={onClickVerify}
        rightSlot={
          verifyResult
            ? (
              <span
                className={
                  'inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 '
                  + 'text-[10px] font-semibold '
                  + (verifyTotal > 0
                    ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-200'
                    : 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200')
                }
                data-testid="action-verify-badge"
                title={
                  verifyTotal > 0
                    ? `${verifyTotal} verify issue(s) — click to refresh`
                    : 'Verify clean'
                }
              >
                {verifyTotal > 0
                  ? <AlertTriangle className="h-2.5 w-2.5" />
                  : <CheckCircle2 className="h-2.5 w-2.5" />}
                <span className="ml-0.5 tabular-nums">{verifyTotal}</span>
              </span>
            )
            : undefined
        }
      />
      <ActionButton
        icon={
          vectorizing
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Wand2 className="h-3.5 w-3.5" />
        }
        label={figure?.is_vectorized ? 'Re-vectorize' : 'Vectorize'}
        disabled={disabled || vectorizing || !!figure?.is_frozen}
        tooltip={figure?.is_frozen
          ? 'Unfreeze the figure first.'
          : (figure?.is_vectorized
            ? 'Re-trace the raster (overwrites the SVG vector paths). Annotation overlays (arrows, text) are preserved.'
            : 'Trace the raster into an editable SVG so individual paths can be selected and deleted.')}
        testid="action-vectorize"
        onClick={onClickVectorize}
      />
      {/* WISHLIST_REGENERATE_FIGURE Phase 16 — Regenerate Image
          button. Tooltip varies with ``source_quality`` so the
          drafter sees a contextual hint (clean = stylistic
          variants only; non-clean = clean up the source).
          Disabled when no figure is selected or the figure is
          frozen — the backend rejects both paths anyway, but
          surfacing the gate at the affordance level avoids the
          rejection. */}
      <ActionButton
        icon={<RefreshCw className="h-3.5 w-3.5" />}
        label="Regenerate Image"
        disabled={disabled || !!figure?.is_frozen}
        tooltip={(() => {
          if (figure?.is_frozen) return 'Unfreeze the figure first.';
          const q = figure?.source_quality || 'unknown';
          switch (q) {
            case 'color_photo':
              return 'Source is a colour photograph. Regenerate as a patent line drawing.';
            case 'screenshot':
              return 'Source appears to be a screenshot. Regenerate as a patent line drawing.';
            case 'sketch':
              return 'Source is a hand sketch. Regenerate as a clean patent line drawing.';
            case 'clean':
              return 'Source is already clean. Regenerate produces stylistic variants.';
            default:
              return 'Regenerate this figure as a patent-quality line drawing.';
          }
        })()}
        testid="action-regenerate-image"
        onClick={() => setRegenerateOpen(true)}
        rightSlot={
          figure && (figure.regeneration_count || 0) > 0
            ? (
              <span
                className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-zinc-100 px-1 text-[10px] font-semibold text-zinc-600 ring-1 ring-zinc-200 tabular-nums"
                data-testid="action-regenerate-count"
                title={`Regenerated ${figure.regeneration_count} time(s); original preserved.`}
              >
                {figure.regeneration_count}
              </span>
            )
            : undefined
        }
      />
      {/* BUG-DRAW-003 — freeze button gated on description.
          The backend enforces this with 422 description_required;
          the FE check is a UX nicety so the drafter sees the
          requirement BEFORE clicking. Empty description → button
          disabled + tooltip points at the Description field on
          the right rail. Unfreezing is never gated (the drafter
          might be unfreezing PRECISELY to add a description). */}
      <ActionButton
        icon={
          freezing
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Lock className="h-3.5 w-3.5" />
        }
        label={figure?.is_frozen ? 'Unfreeze' : 'Freeze'}
        disabled={
          disabled
          || freezing
          || (!figure?.is_frozen
              && !(figure?.imported_description || '').trim())
        }
        tooltip={
          figure?.is_frozen
            ? 'Unfreeze this figure to edit its analysis or canvas again.'
            : !(figure?.imported_description || '').trim()
              ? 'Add a description in the Description field in the '
                + 'left panel before freezing — it goes into the '
                + "specification's Brief Description of the Drawings."
              : 'Freeze this figure. The specification is written from frozen figures.'
        }
        testid={figure?.is_frozen ? 'action-unfreeze' : 'action-freeze'}
        onClick={figure?.is_frozen ? onClickUnfreeze : onClickFreeze}
      />
      {/* WISHLIST_REGENERATE_FIGURE Phase 16 — modal render.
          Mounted inside the action bench so the dialog inherits
          the canvas's stacking context; the inner component
          renders into a fixed-position layer via its own
          z-50 root. */}
      {regenerateOpen && figure && (
        <RegenerateFigurePanel
          matterId={matterId}
          figure={figure}
          onClose={() => setRegenerateOpen(false)}
        />
      )}
      {pendingFreezeIssues !== null && (
        <ConfirmFreezeDialog
          issues={pendingFreezeIssues}
          submitting={freezing}
          onCancel={onCancelFreeze}
          onMapTermsFirst={onMapTermsFirst}
          onFreezeAnyway={onConfirmFreezeAnyway}
        />
      )}
    </div>
  );
}

function ActionButton({
  icon, label, disabled, tooltip, testid, onClick, rightSlot,
}: {
  icon: React.ReactNode;
  label: string;
  disabled: boolean;
  tooltip: string;
  testid: string;
  onClick?: () => void;
  rightSlot?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className="inline-flex h-8 items-center gap-1.5 rounded-sm border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-600 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
      disabled={disabled}
      title={tooltip}
      aria-label={tooltip}
      data-testid={testid}
      onClick={onClick}
    >
      {icon}
      {label}
      {rightSlot}
    </button>
  );
}

// ─── Freeze with-issues confirm dialog (slice 8E.2.b) ─────────────

// Exported for confirmFreeze.test.tsx.
export function ConfirmFreezeDialog({
  issues, submitting, onCancel, onMapTermsFirst, onFreezeAnyway,
}: {
  issues: VerifyIssue[];
  submitting: boolean;
  onCancel: () => void;
  onMapTermsFirst: () => void;
  onFreezeAnyway: () => void;
}) {
  const groupedCounts = issues.reduce<Record<string, number>>(
    (acc, i) => {
      acc[i.kind] = (acc[i.kind] ?? 0) + 1;
      return acc;
    },
    {},
  );
  // BUG-006 — drafters previously saw bare kind names ("2
  // unmapped_term") with no hint about WHERE to fix them; cycle
  // of verify/freeze/verify/freeze ensued. Each kind now carries
  // a one-line action hint mirroring the existing verify-pass
  // remediation paths (auto-map, canvas edit, claims tab, etc.).
  // Plurals are spelled out per kind: a trailing 's' on the whole
  // phrase gave "frozen claim with no figure bindings".
  const ISSUE_LABEL: Record<string, { label: string; plural: string; hint: string }> = {
    unmapped_term:
      { label: 'unmapped claim term', plural: 'unmapped claim terms', hint: 'Use Auto-map or bind manually in the terms panel.' },
    orphan_numeral:
      { label: 'orphan numeral on canvas', plural: 'orphan numerals on canvas', hint: 'Delete the chip or re-link it to a registry numeral.' },
    numeral_no_claim:
      { label: 'registry numeral with no claim term', plural: 'registry numerals with no claim term', hint: 'Map the numeral to a claim term or remove it.' },
    claim_no_numeral:
      { label: 'frozen claim with no figure binding', plural: 'frozen claims with no figure binding', hint: 'Bind a numeral to this claim (Numerals tab → edit → Bind to claim term), or run Analyze.' },
    duplicate_label:
      { label: 'duplicate numeral label', plural: 'duplicate numeral labels', hint: 'Rename the duplicates so labels are unique within the matter.' },
    stale_numeral:
      { label: 'stale numeral', plural: 'stale numerals', hint: 'Reconcile via the cross-figure consistency panel.' },
  };
  const summary = Object.entries(groupedCounts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, n]) => {
      const fallback = k.replace(/_/g, ' ');
      const meta = ISSUE_LABEL[k] ?? { label: fallback, plural: `${fallback}s`, hint: '' };
      const text = `${n} ${n === 1 ? meta.label : meta.plural}`;
      return meta.hint ? `${text} — ${meta.hint}` : text;
    });
  // The brief's auto-map suggestion target is unmapped_term issues —
  // when there are none, "Map terms first" doesn't help and we hide
  // it (Cancel is the right next step instead).
  const hasUnmappedTerms = (groupedCounts.unmapped_term ?? 0) > 0;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-zinc-900/30 px-4 pt-24"
      onMouseDown={(e) => {
        if (!submitting && e.target === e.currentTarget) onCancel();
      }}
      data-testid="confirm-freeze-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-freeze-title"
    >
      <div className="w-full max-w-md rounded-md border border-zinc-200 bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
          <h2
            id="confirm-freeze-title"
            className="flex items-center gap-2 text-sm font-semibold text-zinc-900"
          >
            <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden />
            Freeze figure with {issues.length} verify issue{issues.length === 1 ? '' : 's'}?
          </h2>
        </header>
        <div className="space-y-2 px-4 py-3">
          <p className="text-[12px] text-zinc-600">
            The verify pass found these open issues. Apart from orphan
            numerals, they apply to the whole matter, not just this
            figure:
          </p>
          <ul className="list-inside list-disc text-[12px] text-zinc-700" data-testid="confirm-freeze-summary">
            {summary.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <p className="text-[11px] text-zinc-500">
            Freezing now records the issue list in the figure&apos;s
            history so the Detailed Description step can flag the
            figure for review.
          </p>
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-zinc-200 px-4 py-3">
          <button
            type="button"
            className="inline-flex h-9 items-center justify-center gap-1 rounded-sm border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            onClick={onCancel}
            disabled={submitting}
            data-testid="confirm-freeze-cancel"
          >
            Cancel
          </button>
          {hasUnmappedTerms && (
            <button
              type="button"
              className="inline-flex h-9 items-center justify-center gap-1 rounded-sm border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
              onClick={onMapTermsFirst}
              disabled={submitting}
              data-testid="confirm-freeze-map-first"
            >
              Map terms first
            </button>
          )}
          <button
            type="button"
            className="inline-flex h-9 items-center justify-center gap-2 rounded-sm bg-amber-600 px-4 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
            onClick={onFreezeAnyway}
            disabled={submitting}
            data-testid="confirm-freeze-anyway"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Freeze anyway
          </button>
        </footer>
      </div>
    </div>
  );
}

// ─── Status strip ────────────────────────────────────────────────

function _renderIssueSummary(issue: VerifyIssue): string {
  // Compact one-line description of the issue, used by the
  // status-strip popover. Kind is rendered separately as a
  // label; this function just produces the per-kind body
  // copy. Mirrors the old VerifyFindingsPanel renderer but
  // strips JSX / icons / action buttons — the popover is
  // a read-only summary surface, not an action site.
  switch (issue.kind) {
    case "unmapped_term":
      return `term "${issue.claim_term}" is not bound to any numeral.`;
    case "orphan_numeral":
      return (
        `numeral ${issue.numeral_hint ?? "?"} `
        + `(${issue.label_hint ?? "no label"}) is on the figure `
        + "but not in the registry."
      );
    case "numeral_no_claim":
      return (
        `numeral ${issue.numeral}`
        + (issue.label ? ` (${issue.label})` : "")
        + " binds to no claim term."
      );
    case "claim_no_numeral":
      return (
        `claim ${issue.claim_number} has no figure showing any `
        + "of its terms."
      );
    case "duplicate_label":
      return (
        `label "${issue.label}" is shared by `
        + `numerals ${issue.numerals.join(", ")}.`
      );
    case "stale_numeral":
      return (
        `numeral ${issue.numeral}`
        + (issue.label ? ` (${issue.label})` : "")
        + " was edited after a freeze; the registry row may be stale."
      );
  }
}


export function StatusStrip({
  figure, primitives, numerals, figures,
}: {
  figure: DrawingFigureSummary | null;
  primitives: CanvasPrimitive[];
  numerals: DrawingNumeralRow[];
  figures: DrawingFigureSummary[];
}) {
  const setActiveFigureId = useDrawingStore((s) => s.setActiveFigureId);
  const latestVerifyByFigure = useDrawingStore(
    (s) => s.latestVerifyByFigure,
  );
  // Verify-issues popover state — local to the status strip.
  // Replaces the dismiss/revert dance of the retired
  // VerifyFindingsPanel. Drafter clicks the issue count to
  // reveal the popover; clicks the ✕ (or outside) to dismiss.
  // The popover NEVER auto-appears — only the status-strip
  // count surfaces after a verify run lands.
  const [showIssueList, setShowIssueList] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  // Outside-click dismiss. ``pointerdown`` (not click) so the
  // popover dismisses ahead of any click handler that might
  // otherwise re-open it on the same gesture.
  useEffect(() => {
    if (!showIssueList) return;
    const onPointerDown = (e: PointerEvent) => {
      if (
        popoverRef.current
        && !popoverRef.current.contains(e.target as Node)
      ) {
        setShowIssueList(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener(
      "pointerdown", onPointerDown,
    );
  }, [showIssueList]);

  if (!figure) {
    return (
      <div
        className="flex items-center justify-between border-t border-zinc-200 bg-zinc-50 px-3 py-1.5 text-[11px] text-zinc-400"
        data-testid="canvas-status-strip"
      >
        <span>No figure selected.</span>
        <FigurePager figures={figures} active={null} onSelect={setActiveFigureId} />
      </div>
    );
  }

  // Slice 6D pulls live counts from the canvas-state cache so the
  // strip stays in lockstep with on-canvas mutations (the
  // /figures summary count only refreshes on full reload).
  const numeralChipsHere = primitives.filter((p) => p.primitive_type === 'numeral').length;
  const orphanChipsHere = primitives.filter(
    (p) => p.primitive_type === 'numeral' && !p.drawing_numeral_id,
  ).length;
  const matterTotalNumerals = numerals.length;
  const analyzed = !!figure.analyze_bundle_summary;
  const isFrozen = !!figure.is_frozen;
  // Slice 8F.2 — verify-derived total + per-kind breakdown.
  // Reads the slice 8E.1 store cache; non-null means a verify
  // pass has run for this figure during this session.
  const verifyResult = latestVerifyByFigure[figure.id];
  const verifyTotal = verifyResult?.total ?? 0;

  return (
    <div
      className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-zinc-200 bg-zinc-50 px-3 py-1.5 text-[11px] text-zinc-600"
      data-testid="canvas-status-strip"
    >
      {/* Phase 6H dot indicators per brief §8. ● filled = state on,
          ○ outline = state off / pending. */}
      <DotIndicator on label={`origin: ${figure.origin}`} testid="dot-imported" />
      <span aria-hidden className="text-zinc-300">·</span>
      <DotIndicator on={analyzed} label={analyzed ? 'analyzed' : 'not analyzed'}
                    testid="dot-analyzed" />
      {verifyResult && (
        <>
          <span aria-hidden className="text-zinc-300">·</span>
          <div className="relative inline-block">
            <button
              type="button"
              className={[
                "underline-offset-2 hover:underline focus:outline-none focus:underline",
                verifyTotal > 0
                  ? "text-amber-700 hover:text-amber-900"
                  : "text-emerald-700 hover:text-emerald-900",
              ].join(" ")}
              data-testid="verify-status"
              title={
                verifyTotal === 0
                  ? "Verify clean."
                  : (
                    Object.entries(verifyResult.counts)
                      .filter(([, n]) => n > 0)
                      .map(([k, n]) => `${n} ${k.replace(/_/g, " ")}`)
                      .join(" · ")
                    + " — click to view the findings."
                  )
              }
              // Click-to-toggle popover. ``verifyTotal === 0``
              // also opens the popover (a one-line "no issues
              // found" confirmation) so the click affordance
              // doesn't go silent on a clean run.
              onClick={() => setShowIssueList((s) => !s)}
            >
              {verifyTotal === 0
                ? "✓ verify clean"
                : `⚠ ${verifyTotal} verify issue${verifyTotal === 1 ? "" : "s"}`}
            </button>
            {showIssueList && (
              <div
                ref={popoverRef}
                role="dialog"
                aria-label="Verify findings"
                data-testid="verify-issue-popover"
                className="absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 w-80 max-h-72 overflow-y-auto rounded-md border border-zinc-300 bg-white shadow-lg"
              >
                <header className="flex items-center justify-between border-b border-zinc-200 px-3 py-1.5">
                  <span className={[
                    "text-[11px] font-semibold uppercase tracking-wider",
                    verifyTotal > 0
                      ? "text-amber-700"
                      : "text-emerald-700",
                  ].join(" ")}>
                    {verifyTotal === 0
                      ? "Verify clean"
                      : `${verifyTotal} verify finding${verifyTotal === 1 ? "" : "s"}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowIssueList(false)}
                    aria-label="Close verify findings popover"
                    className="rounded-sm p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                    data-testid="verify-issue-popover-close"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </header>
                <div className="px-3 py-2">
                  {verifyTotal === 0 ? (
                    <p className="text-[11px] text-zinc-500">
                      No issues found in the most recent verify
                      run. Re-run from the action bench after
                      any change.
                    </p>
                  ) : (
                    <ul className="space-y-1.5" role="list">
                      {verifyResult.issues.slice(0, 12).map((issue, i) => (
                        <li
                          key={`${issue.kind}-${i}`}
                          className="rounded-sm border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-zinc-800"
                          data-testid={`verify-issue-popover-row-${i}`}
                        >
                          <span className="font-mono uppercase text-amber-700">
                            {issue.kind.replace(/_/g, " ")}
                          </span>
                          <span className="ml-1 text-zinc-700">
                            {_renderIssueSummary(issue)}
                          </span>
                        </li>
                      ))}
                      {verifyResult.issues.length > 12 && (
                        <li className="text-[10px] italic text-zinc-500">
                          …and {verifyResult.issues.length - 12} more.
                          Run Verify again from the toolbar after
                          resolving these.
                        </li>
                      )}
                    </ul>
                  )}
                  {/* NB-36 — advisory, not an issue: freezing is not
                      refused for it. */}
                  {(verifyResult.warnings || []).length > 0 && (
                    <ul className="mt-2 space-y-1.5" role="list"
                        data-testid="verify-warning-list">
                      {(verifyResult.warnings || []).map((w, i) => (
                        <li
                          key={`warning-${i}`}
                          className="rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1 text-[11px] text-zinc-800"
                          data-testid={`verify-warning-row-${i}`}
                        >
                          <span className="font-medium text-zinc-600">
                            Check:
                          </span>
                          <span className="ml-1 text-zinc-700">{w.message}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
      <span aria-hidden className="text-zinc-300">·</span>
      <span data-testid="numeral-count">
        {numeralChipsHere} numeral{numeralChipsHere === 1 ? '' : 's'}
      </span>
      {/* BUG-2 — AI-generated figures bake numeral labels into the
          PNG; if the auto-extract on accept didn't register any
          (LLM failure, empty result), the strip would just read
          "0 numerals" with no recovery affordance. Surface a
          hint pointing the drafter at Auto-map so they know what
          to click. Gated on origin AND a zero count AND no
          orphans (orphans get their own amber chip below). */}
      {figure.origin === 'ai_generated'
        && numeralChipsHere === 0
        && orphanChipsHere === 0 && (
        <>
          <span aria-hidden className="text-zinc-300">·</span>
          <span
            className="text-amber-700"
            data-testid="numerals-not-extracted-hint"
            title="This AI figure has numerals drawn into the image,
              but none are linked to it yet. Click Auto-map in the
              action bar to find them."
          >
            Run Auto-map to find its numerals
          </span>
        </>
      )}
      {orphanChipsHere > 0 && (
        <>
          <span aria-hidden className="text-zinc-300">·</span>
          <span className="text-amber-700" data-testid="orphan-count">
            {orphanChipsHere} orphan
          </span>
        </>
      )}
      {matterTotalNumerals > 0 && (
        <>
          <span aria-hidden className="text-zinc-300">·</span>
          <span className="text-zinc-500">{matterTotalNumerals} in registry</span>
        </>
      )}
      {figure.unmapped_terms_count > 0 && (
        <>
          <span aria-hidden className="text-zinc-300">·</span>
          <span className="text-amber-700" data-testid="unmapped-count">
            {figure.unmapped_terms_count} unmapped
          </span>
        </>
      )}
      {figure.source_stale && (
        <>
          <span aria-hidden className="text-zinc-300">·</span>
          <span className="text-amber-700">source updated</span>
        </>
      )}
      <span aria-hidden className="text-zinc-300">·</span>
      <DotIndicator on={isFrozen} label={isFrozen ? 'frozen' : 'not frozen'}
                    testid="dot-frozen" />
      <span className="ml-auto" />
      <FigurePager figures={figures} active={figure} onSelect={setActiveFigureId} />
    </div>
  );
}

function DotIndicator({
  on, label, testid,
}: {
  on: boolean;
  label: string;
  testid?: string;
}) {
  return (
    <span className="inline-flex items-center gap-1" data-testid={testid} data-on={on}>
      <span
        className={[
          'h-1.5 w-1.5 rounded-full',
          on ? 'bg-green-600' : 'border border-zinc-400',
        ].join(' ')}
        aria-hidden
      />
      <span>{label}</span>
    </span>
  );
}

function FigurePager({
  figures, active, onSelect,
}: {
  figures: DrawingFigureSummary[];
  active: DrawingFigureSummary | null;
  onSelect: (figureId: string) => void;
}) {
  if (figures.length === 0) return null;
  // Slice 6H — cycle through IN DRAWING figures by sequence_num
  // (the gallery already orders the same way; we mirror that order
  // here so the pager and the sidebar stay in lockstep).
  const ordered = [...figures].sort(
    (a, b) => (a.sequence_num ?? 0) - (b.sequence_num ?? 0),
  );
  const idx = active ? ordered.findIndex((f) => f.id === active.id) : -1;
  const total = ordered.length;
  const onPrev = () => {
    if (total === 0) return;
    const next = idx <= 0 ? total - 1 : idx - 1;
    onSelect(ordered[next].id);
  };
  const onNext = () => {
    if (total === 0) return;
    const next = idx === -1 || idx === total - 1 ? 0 : idx + 1;
    onSelect(ordered[next].id);
  };
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500"
      data-testid="figure-pager"
    >
      <span className="font-medium">
        {idx >= 0 ? `Fig. ${idx + 1} of ${total}` : `${total} figure${total === 1 ? '' : 's'}`}
      </span>
      <button
        type="button"
        onClick={onPrev}
        disabled={total < 2}
        className="inline-flex h-5 w-5 items-center justify-center rounded-sm border border-zinc-200 bg-white hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40"
        title="Previous figure"
        aria-label="Previous figure"
        data-testid="figure-pager-prev"
      >
        ‹
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={total < 2}
        className="inline-flex h-5 w-5 items-center justify-center rounded-sm border border-zinc-200 bg-white hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40"
        title="Next figure"
        aria-label="Next figure"
        data-testid="figure-pager-next"
      >
        ›
      </button>
    </span>
  );
}
